import { useState, useEffect, useCallback, useMemo, useRef} from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, RadialBarChart, RadialBar
} from "recharts";
import { useTheme } from "../context/ThemeContext";
import { useLanguage, getLang } from "../lang/useLanguage";
import { getStatusColors } from "../context/themes";

// ─── CONFIG ────────────────────────────────────────────────────────────────
const API = '/api';
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const getLocale = () => {
  const lang = localStorage.getItem("lang") || localStorage.getItem("language") || "tr";
  return lang === "en" ? "en-US" : "tr-TR";
};

const fmt = (d) => d ? new Date(d).toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDT = (value) => {
  if (!value) return "—";

  let normalized = value;

  if (typeof value === "string") {
    normalized = value.trim().replace(" ", "T");

    const hasTimezone =
      /Z$/i.test(normalized) ||
      /[+-]\d{2}:?\d{2}$/.test(normalized);

    if (!hasTimezone) {
      normalized += "Z";
    }
  }

  const parsed = new Date(normalized);

  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat(getLocale(), {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(parsed);
};

const MACHINE_STALE_AFTER_MS = 15 * 1000;
const MACHINE_OFFLINE_AFTER_MS = 30 * 1000;

const parseMachineDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  let normalized = value.trim().replace(" ", "T");

  /*
   * Backend last_seen alanını UTC_TIMESTAMP() ile üretir ancak MySQL
   * DATETIME/ISO cevabında çoğu zaman Z veya saat dilimi bulunmaz.
   * Saat dilimi olmayan değerleri UTC kabul etmezsek tarayıcı bunları
   * yerel saat olarak yorumlar ve Türkiye'de veriyi yaklaşık 3 saat
   * eski sanarak makineyi yanlışlıkla offline gösterir.
   */
  const hasTimezone =
    /Z$/i.test(normalized) ||
    /[+-]\d{2}:?\d{2}$/.test(normalized);

  if (!hasTimezone) {
    normalized += "Z";
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getMachineFreshness = (machine, now = Date.now()) => {
  const lastSeenDate = parseMachineDate(machine?.last_seen);
  const hasLastSeen = Boolean(lastSeenDate);

  const ageMs = hasLastSeen
    ? Math.max(0, now - lastSeenDate.getTime())
    : null;

  const hasFreshTelemetry =
    hasLastSeen &&
    ageMs < MACHINE_OFFLINE_AFTER_MS;

  const backendSaysOffline =
    machine?.connection_status === "offline" ||
    machine?.status === "offline";

  /*
   * En güvenilir işaret gerçek telemetry zamanıdır.
   *
   * Son 30 saniye içinde veri geldiyse backend üzerinde kalmış eski
   * offline alanı bağlantıyı geçersiz kılamaz.
   *
   * Bu kural hem simülasyon hem gerçek Edge makineleri için geçerlidir.
   */
  const explicitOffline =
    backendSaysOffline && !hasFreshTelemetry;

  const expectsLiveData = Boolean(
    machine?.edge_enabled ||
    machine?.edge_machine_uid ||
    machine?.driver ||
    machine?.protocol ||
    machine?.connection_status ||
    machine?.source ||
    machine?.last_seen
  );

  const missingLiveTimestamp =
    expectsLiveData && !hasLastSeen;

  const isOffline =
    explicitOffline ||
    missingLiveTimestamp ||
    (ageMs != null && ageMs >= MACHINE_OFFLINE_AFTER_MS);

  const isStale =
    !isOffline &&
    ageMs != null &&
    ageMs >= MACHINE_STALE_AFTER_MS;

  return {
    state: isOffline ? "offline" : isStale ? "stale" : "online",
    isOffline,
    isStale,
    ageMs,
    hasLastSeen,
    hasFreshTelemetry,
  };
};

const deriveMachineDisplayStatus = (machine, now = Date.now()) => {
  const freshness = getMachineFreshness(machine, now);

  if (freshness.isOffline) return "offline";

  const rawStatus = String(
    machine?.status || "unknown"
  ).trim().toLowerCase();

  const alarmCode = Number(machine?.alarm_code ?? 0);

  const hasAlarmMessage =
    typeof machine?.alarm_message === "string" &&
    machine.alarm_message.trim() !== "" &&
    machine.alarm_message.trim() !== "0";

  const hasAlarm =
    rawStatus === "fault" ||
    rawStatus === "alarm" ||
    (Number.isFinite(alarmCode) && alarmCode > 0) ||
    hasAlarmMessage;

  if (hasAlarm) return "fault";

  const emergencyActive =
    Number(machine?.emergency ?? machine?.native_emergency ?? 0) > 0;

  if (emergencyActive) return "fault";

  /*
   * Stale veri, makinenin gerçekten durmuş olduğu anlamına gelmez.
   * Bu yüzden running göstermeyip güvenli biçimde idle görünümüne düşürüyoruz.
   */
  if (freshness.isStale) return "idle";

  const backendStatus = machine?.status || "unknown";

  /*
   * Telemetry taze olduğu halde DB üzerinde eski offline durumu kaldıysa
   * canlı ölçümlerden gerçek çalışma durumunu türet.
   */
  if (backendStatus === "offline" && freshness.hasFreshTelemetry) {
    const rpm = Number(
      machine?.spindle_rpm ??
      machine?.spindle_speed ??
      machine?.rpm ??
      0
    );

    const feed = Number(machine?.feed_rate ?? 0);

    const motion =
      Number(
        machine?.motion ??
        machine?.native_motion ??
        0
      ) > 0;

    const run =
      Number(
        machine?.run ??
        machine?.native_run ??
        0
      ) > 0;

    if (
      (Number.isFinite(rpm) && rpm > 0) ||
      (Number.isFinite(feed) && feed > 0) ||
      motion ||
      run
    ) {
      return "running";
    }

    return "stopped";
  }

  return backendStatus;
};

const withMachineRuntimeState = (machine, now = Date.now()) => {
  const freshness = getMachineFreshness(machine, now);
  const displayStatus = deriveMachineDisplayStatus(machine, now);

  return {
    ...machine,
    _freshness_state: freshness.state,
    _is_offline: freshness.isOffline,
    _is_stale: freshness.isStale,
    _last_seen_age_ms: freshness.ageMs,
    _display_status: displayStatus,
  };
};

const formatLastSeenAge = (machine) => {
const m = getLang().machinesx || {};

  // MACHINES_CLOCK_SAFE_P7
  // Runtime state was calculated against API server_time in fetchMachines().
  // Never recalculate last_seen age against the browser clock here.
  const hasLastSeen = Boolean(parseMachineDate(machine?.last_seen));

  if (!hasLastSeen) return m.dataUnavailable;

  const ageMs = Number.isFinite(machine?._last_seen_age_ms)
    ? machine._last_seen_age_ms
    : 0;

  const seconds = Math.floor(Math.max(0, ageMs) / 1000);

  if (seconds < 5) return m.now;
  if (seconds < 60) return `${seconds} ${m.secondsAgo}`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} ${m.minutesAgo}`;

  const hours = Math.floor(minutes / 60);
  return `${hours} ${m.hoursAgo}`;
};

const getStatusLabels = () => {
  const l = getLang();
  const m = l.machinesx || {};
  return {
    running:        m.statusRunning,
    idle:           m.statusIdle,
    offline:        m.statusOffline        || m.connectionLost,
    stale:          m.statusStale,
    maintenance:    m.statusMaintenance,
    fault:          m.statusFault,
    alarm:          m.statusAlarm,
    setup:          m.statusSetup,
    tool_change:    m.statusToolChange,
    stopped:        m.statusStopped,
    unknown:        m.statusUnknown,
  };
};

// ─── STATUS BADGE ──────────────────────────────────────────────────────────
const Badge = ({ status }) => {
  const { themeName } = useTheme();
  const c = getStatusColors(status, themeName);
  const labels = getStatusLabels();
  const label = labels[status] || status;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.color,
      border: `1px solid ${c.color}44`,
      letterSpacing: "0.02em",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%", background: c.color,
        boxShadow: `0 0 6px ${c.color}`,
        animation: status === "running" ? "sf-pulse 2s infinite" : "none",
      }} />
      {label}
    </span>
  );
};

// ─── METRIC CARD ──────────────────────────────────────────────────────────
const MetricCard = ({ label, value, unit, color, icon, sub }) => {
  const { t } = useTheme();
  return (
    <div style={{
      background: t.bg.metricCard, border: `1px solid ${t.border.default}`,
      borderRadius: 12, padding: "16px 20px", flex: 1, minWidth: 140,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ color: t.text.label, fontSize: 11, fontWeight: 700,
            letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: color || t.accent.blue, fontVariantNumeric: "tabular-nums" }}>
            {value ?? "—"}
            <span style={{ fontSize: 13, fontWeight: 500, color: t.text.muted, marginLeft: 4 }}>{unit}</span>
          </div>
          {sub && <div style={{ color: t.text.muted, fontSize: 11, marginTop: 4 }}>{sub}</div>}
        </div>
        {icon && <span style={{ fontSize: 22, opacity: 0.7 }}>{icon}</span>}
      </div>
    </div>
  );
};

// ─── OEE GAUGE ────────────────────────────────────────────────────────────
const OeeGauge = ({ value, t }) => {
  const color = value >= 85 ? t.accent.green : value >= 65 ? t.accent.yellow : t.accent.red;
  return (
    <div style={{ position: "relative", width: 120, height: 120 }}>
      <RadialBarChart width={120} height={120} innerRadius={40} outerRadius={56}
        data={[{ value: value ?? 0, fill: color }]} startAngle={225} endAngle={-45}>
        <RadialBar dataKey="value" cornerRadius={4} />
      </RadialBarChart>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center" }}>
        <div style={{ fontSize: 20, fontWeight: 800, color }}>{value ?? "—"}</div>
        <div style={{ fontSize: 10, color: t.text.muted, fontWeight: 600 }}>OEE %</div>
      </div>
    </div>
  );
};

// ─── MACHINE DETAIL MODAL ─────────────────────────────────────────────────
const ScadaGauge = ({
  value,
  displayValue,
  unit,
  maxValue,
  color,
  offline,
  t,
}) => {
  const numericValue = Number(value);
  const safeMax = Math.max(Number(maxValue) || 1, 1);

  const ratio =
    offline || !Number.isFinite(numericValue)
      ? 0
      : Math.min(Math.max(numericValue / safeMax, 0), 1);

  const radius = 86;
  const circumference = Math.PI * radius;
  const progress = circumference * ratio;

  return (
    <div style={{
      width: "100%",
      maxWidth: 310,
      margin: "14px auto 0",
      position: "relative",
    }}>
      <svg
        viewBox="0 0 220 130"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          overflow: "visible",
        }}
      >
        <path
          d="M 24 108 A 86 86 0 0 1 196 108"
          fill="none"
          stroke={t.border.subtle}
          strokeWidth="15"
          strokeLinecap="round"
        />

        <path
          d="M 24 108 A 86 86 0 0 1 196 108"
          fill="none"
          stroke={offline ? t.text.placeholder : color}
          strokeWidth="15"
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          style={{
            filter: offline
              ? "none"
              : `drop-shadow(0 0 5px ${color})`,
            transition:
              "stroke-dasharray 700ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        />

        {[0, 0.25, 0.5, 0.75, 1].map((tick, index) => {
          const angle = Math.PI - tick * Math.PI;
          const x1 = 110 + Math.cos(angle) * 69;
          const y1 = 108 - Math.sin(angle) * 69;
          const x2 = 110 + Math.cos(angle) * 77;
          const y2 = 108 - Math.sin(angle) * 77;

          return (
            <line
              key={index}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={t.text.muted}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          );
        })}

        {!offline && Number.isFinite(numericValue) && (
          <g
            style={{
              transform: `rotate(${-90 + ratio * 180}deg)`,
              transformOrigin: "110px 108px",
              transition:
                "transform 700ms cubic-bezier(0.2, 0.8, 0.2, 1)",
            }}
          >
            <line
              x1="110"
              y1="108"
              x2="110"
              y2="42"
              stroke={color}
              strokeWidth="4"
              strokeLinecap="round"
            />

            <circle
              cx="110"
              cy="108"
              r="8"
              fill={color}
              style={{
                filter: `drop-shadow(0 0 4px ${color})`,
              }}
            />
          </g>
        )}

        <text
          x="110"
          y="88"
          textAnchor="middle"
          fill={offline ? t.text.placeholder : color}
          fontSize="30"
          fontWeight="900"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {displayValue}
        </text>

        <text
          x="110"
          y="108"
          textAnchor="middle"
          fill={offline ? t.text.placeholder : "#f8fafc"}
          fontSize="13"
          fontWeight="900"
          letterSpacing="0.4"
          style={{
            paintOrder: "stroke",
            stroke: "rgba(15, 23, 42, 0.85)",
            strokeWidth: 2,
            textShadow: offline
              ? "none"
              : `0 0 8px ${color}88`,
          }}
        >
          {displayValue !== "--" ? unit : ""}
        </text>

        <text
          x="24"
          y="126"
          textAnchor="middle"
          fill={t.text.placeholder}
          fontSize="8"
          fontWeight="700"
        >
          0
        </text>

        <text
          x="196"
          y="126"
          textAnchor="middle"
          fill={t.text.placeholder}
          fontSize="8"
          fontWeight="700"
        >
          {Number(safeMax).toLocaleString(getLocale())}
        </text>
      </svg>
    </div>
  );
};


// ─── SCADA V4 PANELS ────────────────────────────────────────────────────────

const ScadaPanelTitle = ({ icon, children, color, t }) => (
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 15,
    color: color || t.text.label,
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  }}>
    <span style={{ fontSize: 15 }}>{icon}</span>
    <span>{children}</span>
  </div>
);

const ScadaDataRow = ({ label, value, color, t, strong = false }) => (
  <div style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 14,
    minHeight: 29,
    borderBottom: `1px solid ${t.border.subtle}`,
  }}>
    <span style={{
      color: t.text.muted,
      fontSize: 10,
      fontWeight: 700,
    }}>
      {label}
    </span>

    <span style={{
      color: color || t.text.primary,
      fontSize: strong ? 14 : 12,
      fontWeight: strong ? 900 : 700,
      textAlign: "right",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "65%",
      fontVariantNumeric: "tabular-nums",
    }}>
      {value ?? "—"}
    </span>
  </div>
);

const ScadaOeeRing = ({ label, value, color, t }) => {
  const numeric = Number(value);
  const safeValue = Number.isFinite(numeric)
    ? Math.min(Math.max(numeric, 0), 100)
    : 0;

  const radius = 29;
  const circumference = 2 * Math.PI * radius;
  const progress = circumference * (safeValue / 100);

  return (
    <div style={{
      flex: "1 1 72px",
      minWidth: 67,
      textAlign: "center",
    }}>
      <div style={{
        position: "relative",
        width: 72,
        height: 72,
        margin: "0 auto",
      }}>
        <svg
          viewBox="0 0 72 72"
          style={{
            width: 72,
            height: 72,
            transform: "rotate(-90deg)",
          }}
        >
          <circle
            cx="36"
            cy="36"
            r={radius}
            fill="none"
            stroke={t.border.subtle}
            strokeWidth="7"
          />

          <circle
            cx="36"
            cy="36"
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={`${progress} ${circumference}`}
            style={{
              transition:
                "stroke-dasharray 700ms cubic-bezier(0.2,0.8,0.2,1)",
              filter: `drop-shadow(0 0 4px ${color}88)`,
            }}
          />
        </svg>

        <div style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          fontSize: 16,
          fontWeight: 950,
          fontVariantNumeric: "tabular-nums",
        }}>
          {Number.isFinite(numeric) ? `${Math.round(numeric)}%` : "—"}
        </div>
      </div>

      <div style={{
        marginTop: 7,
        color: t.text.muted,
        fontSize: 8,
        lineHeight: 1.15,
        fontWeight: 800,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
      }}>
        {label}
      </div>
    </div>
  );
};

const ScadaStatusTimeline = ({
  items,
  currentStatus,
  statusLabels,
  themeName,
  t,
  m,
}) => {
  const sourceItems =
    Array.isArray(items) && items.length > 0
      ? items.slice(0, 8).reverse()
      : [{ new_status: currentStatus || "unknown" }];

  return (
    <div>
      <div style={{
        display: "flex",
        gap: 3,
        height: 17,
        overflow: "hidden",
        borderRadius: 6,
        background: t.bg.input,
        border: `1px solid ${t.border.subtle}`,
        padding: 2,
      }}>
        {sourceItems.map((item, index) => {
          const status =
            item.new_status ||
            item.status ||
            currentStatus ||
            "unknown";

          const colors = getStatusColors(status, themeName);

          return (
            <div
              key={`${status}-${index}`}
              title={statusLabels[status] || status}
              style={{
                flex: 1,
                minWidth: 7,
                borderRadius: 4,
                background: colors.color,
                opacity: 0.92,
                boxShadow: `0 0 5px ${colors.color}55`,
              }}
            />
          );
        })}
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 6,
        marginTop: 14,
      }}>
        {[
          ["running", m.filterRunning],
          ["idle", m.filterIdle],
          ["fault", m.filterFault],
          ["stopped", m.filterStopped],
        ].map(([status, label]) => {
          const colors = getStatusColors(status, themeName);

          return (
            <div key={status} style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              minWidth: 0,
            }}>
              <span style={{
                width: 7,
                height: 7,
                borderRadius: 2,
                flexShrink: 0,
                background: colors.color,
              }} />

              <span style={{
                color: t.text.muted,
                fontSize: 8,
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const MachineDetailModal = ({ machine, onClose, onEdit }) => {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
const currentLang =
  localStorage.getItem("lang") ||
  localStorage.getItem("language") ||
  "tr";
  const m = l.machinesx || {};
  const [tab, setTab] = useState("overview");
  const [faults, setFaults] = useState([]);
  const [alarms, setAlarms] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [oeeHistory, setOeeHistory] = useState([]);
  const [spareParts, setSpareParts] = useState([]);
  const [statusLog, setStatusLog] = useState([]);

  // STATUS_LOG_DISPLAY_RC1
  // Aynı fiziksel olay live_event + downtime olarak iki kez
  // gelirse ayrıntılı downtime kaydını tercih eder.
  const displayStatusLog = useMemo(() => {
    if (!Array.isArray(statusLog)) return [];

    const parseTime = (value) => {
      if (!value) return null;

      const normalized = String(value)
        .trim()
        .replace(" ", "T");

      const d = new Date(normalized);

      return Number.isNaN(d.getTime())
        ? null
        : d.getTime();
    };

    const downtimeEvents = statusLog.filter(
      (item) =>
        item?.source === "downtime" ||
        item?.event_type === "downtime"
    );

    return statusLog.filter((item) => {
      const isLiveEvent =
        item?.source === "live_event" ||
        item?.event_source === "telemetry";

      if (!isLiveEvent) {
        return true;
      }

      const liveTime = parseTime(
        item.event_time ||
        item.created_at ||
        item.started_at
      );

      if (liveTime == null) {
        return true;
      }

      const liveStatus = String(
        item.new_status ||
        item.status ||
        ""
      ).toLowerCase();

      const matchingDowntime = downtimeEvents.some(
        (dt) => {
          const dtTime = parseTime(
            dt.event_time ||
            dt.started_at ||
            dt.created_at
          );

          if (dtTime == null) {
            return false;
          }

          const dtStatus = String(
            dt.new_status ||
            dt.status ||
            ""
          ).toLowerCase();

          const sameStatus =
            !liveStatus ||
            !dtStatus ||
            liveStatus === dtStatus;

          // Aynı olayın iki farklı backend kaydı genellikle
          // birkaç saniye içinde oluşur.
          const sameMoment =
            Math.abs(liveTime - dtTime) <= 5000;

          return sameStatus && sameMoment;
        }
      );

      return !matchingDowntime;
    });
  }, [statusLog]);
  const detailScrollRef = useRef(null);

  // Son 60 canlı ölçüm: RPM ve Feed mini trendleri
  const [liveSamples, setLiveSamples] = useState([]);

  useEffect(() => {
    if (!machine) return;

    // MACHINES_CLOCK_SAFE_P7
    // Use server-time-derived runtime state.
    if (machine?._is_offline || machine?._is_stale) return;

    const rpmRaw =
      machine.spindle_speed ??
      machine.spindle_rpm ??
      machine.rpm;

    const feedRaw = machine.feed_rate;

    const rpm = Number(rpmRaw);
    const feed = Number(feedRaw);

    if (!Number.isFinite(rpm) && !Number.isFinite(feed)) return;

    setLiveSamples(previous => {
      const sample = {
        time: Date.now(),
        rpm: Number.isFinite(rpm) ? rpm : 0,
        feed: Number.isFinite(feed) ? feed : 0,
      };

      return [...previous, sample].slice(-60);
    });
  }, [machine]);

  useEffect(() => {
    if (!machine) return;
    const id = machine.machine_id || machine.id;
    const fetchAll = async () => {
      try { const r = await fetch(`${API}/machines/${id}/oee-history?days=7`, { headers: headers() }); if (r.ok) setOeeHistory(await r.json()); } catch {}
      try { const r = await fetch(`${API}/machines/${id}/faults?limit=10`, { headers: headers() }); if (r.ok) setFaults(await r.json()); } catch {}
      try { const r = await fetch(`${API}/maintenance?machine_id=${id}&limit=10`, { headers: headers() }); if (r.ok) { const d = await r.json(); setMaintenances(Array.isArray(d) ? d : d.items || []); } } catch {}
      try { const r = await fetch(`${API}/alarms?machine_id=${id}&limit=10`, { headers: headers() }); if (r.ok) { const d = await r.json(); setAlarms(
            Array.isArray(d)
              ? d
              : d.alarms || d.items || []
          ); } } catch {}
      try { const r = await fetch(`${API}/machines/${id}/spare-parts`, { headers: headers() }); if (r.ok) setSpareParts(await r.json()); } catch {}
      try { const r = await fetch(`${API}/machines/${id}/status-log?limit=20`, { headers: headers() }); if (r.ok) setStatusLog(await r.json()); } catch {}
    };
    fetchAll();
  }, [machine]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (detailScrollRef.current) {
        detailScrollRef.current.scrollTop = 0;
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [machine?.machine_id, machine?.id, tab]);

  if (!machine) return null;

  const tabs = [
    { id: "overview",    label: m.tabOverview },
    { id: "machineinfo", label: m.tabMachineInfo },
    { id: "oee",         label: m.tabOee },
    { id: "faults",      label: m.tabFaults },
    { id: "maintenance", label: m.tabMaintenance },
    { id: "alarms",      label: m.tabAlarms },
    { id: "parts",       label: m.tabParts },
    { id: "statuslog",   label: m.tabStatusLog },
  ];

  const sc = (s) => getStatusColors(s, themeName);
  const statusLabels = getStatusLabels();

  // MACHINES_CLOCK_SAFE_P7
  // Do not derive connection state from the client clock.
  const isOffline = Boolean(machine?._is_offline);
  const isStale = Boolean(machine?._is_stale);

  const liveAlarmStatus = String(
    machine?.status || "unknown"
  ).trim().toLowerCase();

  const liveAlarmCode = Number(machine?.alarm_code ?? 0);

  const liveHasAlarmMessage =
    typeof machine?.alarm_message === "string" &&
    machine.alarm_message.trim() !== "" &&
    machine.alarm_message.trim() !== "0";

  const hasLiveAlarm =
    !isOffline &&
    (
      liveAlarmStatus === "fault" ||
      liveAlarmStatus === "alarm" ||
      (Number.isFinite(liveAlarmCode) && liveAlarmCode > 0) ||
      liveHasAlarmMessage
    );

  const liveState = deriveMachineDisplayStatus(machine);

  const liveColors = sc(liveState);

  const spindleValue =
    machine.spindle_rpm ??
    machine.spindle_speed ??
    machine.rpm;

  const productionValue =
    machine.production_count ??
    machine.production ??
    null;

  const targetValue =
    machine.target_count ??
    machine.production_target ??
    machine.target_quantity;

  const defectiveValue =
    machine.defective_count ??
    machine.defective ??
    machine.scrap_count ??
    machine.reject_count;

  const liveSpindle =
    !isOffline && !isStale && spindleValue != null
      ? Number(spindleValue).toLocaleString(getLocale(), {
          maximumFractionDigits: 0,
        })
      : "--";

  const liveFeed =
    !isOffline && !isStale && machine.feed_rate != null
      ? Number(machine.feed_rate).toLocaleString(getLocale(), {
          maximumFractionDigits: 0,
        })
      : "--";

  const liveCycle =
    !isOffline && !isStale && machine.cycle_count != null
      ? Number(machine.cycle_count).toLocaleString(getLocale())
      : "--";

  const liveProduction =
    !isOffline && !isStale && productionValue != null
      ? Number(productionValue).toLocaleString(getLocale())
      : "--";

  const liveTarget =
    !isOffline && !isStale && targetValue != null
      ? Number(targetValue).toLocaleString(getLocale())
      : "--";

  const liveDefective =
    !isOffline && !isStale && defectiveValue != null
      ? Number(defectiveValue).toLocaleString(getLocale())
      : "--";

  const liveProgram =
    isOffline || isStale
      ? "--"
      : machine.program_no || machine.program_number || "—";

  const liveMode =
    isOffline || isStale
      ? "--"
      : machine.mode || "—";

  const liveLastSeen =
    machine.last_seen
      ? `${fmtDT(machine.last_seen)} · ${formatLastSeenAge(machine)}`
      : m.dataUnavailable;

  const createTrendPoints = (samples, key) => {
    if (!samples || samples.length < 2) return "";

    const values = samples.map(item => Number(item[key]) || 0);
    const maxValue = Math.max(...values, 1);
    const minValue = Math.min(...values, 0);
    const range = Math.max(maxValue - minValue, 1);

    return values
      .map((value, index) => {
        const x =
          values.length === 1
            ? 0
            : (index / (values.length - 1)) * 100;

        const y = 38 - ((value - minValue) / range) * 34;

        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  };

  const rpmTrendPoints = createTrendPoints(liveSamples, "rpm");
  const feedTrendPoints = createTrendPoints(liveSamples, "feed");

  const rpmPeak =
    liveSamples.length > 0
      ? Math.max(...liveSamples.map(item => Number(item.rpm) || 0))
      : null;

  const feedPeak =
    liveSamples.length > 0
      ? Math.max(...liveSamples.map(item => Number(item.feed) || 0))
      : null;

  // SCADA V4 güvenli veri eşlemeleri
  const activeWorkOrderNo =
    machine.active_work_order_no ??
    machine.work_order_no ??
    machine.active_order_no ??
    machine.order_no ??
    "—";

  const activeProduct =
    machine.active_product_name ??
    machine.product_name ??
    machine.part_name ??
    machine.product_code ??
    "—";

  const activeOperator =
    machine.operator_name ??
    machine.active_operator_name ??
    machine.operator ??
    "—";


  const programName =
    machine.program_name ??
    machine.active_program_name ??
    "—";

  const activeTool =
    machine.tool_no ??
    machine.active_tool ??
    machine.current_tool ??
    "—";

  const programLine =
    machine.program_line ??
    machine.sequence_no ??
    machine.block_no ??
    machine.current_block ??
    "—";

  const liveAvailability =
    machine.availability ??
    machine.availability_rate ??
    null;

  const livePerformance =
    machine.performance ??
    machine.performance_rate ??
    null;

  const liveQuality =
    machine.quality_rate ??
    machine.quality ??
    null;

  const liveOee =
    machine.oee ??
    null;

  // MES RC2 üretim ve kalite özeti
  const hasActiveWorkOrder =
    Boolean(machine.active_work_order_id || machine.active_work_order_no);

  const operationStatus =
    machine.operation_status ||
    machine.active_work_order_status ||
    (isOffline ? "offline" : "waiting");

  const operationStatusLabel =
    machine.operation_status_label ||
    (
      operationStatus === "active"
        ? (m.productionRunning)
        : operationStatus === "paused"
        ? (m.productionPaused)
        : operationStatus === "completed"
        ? (m.productionCompleted)
        : operationStatus === "offline"
        ? m.connectionLost
        : m.waitingWorkOrder
    );

  // RC6.6C — Runtime / Work Order gerçek alan eşlemesi
  const workOrderTargetQuantity =
    hasActiveWorkOrder
      ? Number(
          machine.work_order_quantity ??
          machine.work_order_target_quantity ??
          0
        )
      : null;

  const workOrderProducedQuantity =
    hasActiveWorkOrder
      ? Number(
          machine.work_order_produced ??
          machine.work_order_produced_quantity ??
          0
        )
      : null;

  const workOrderDefectiveQuantity =
    hasActiveWorkOrder
      ? Number(
          machine.work_order_defective ??
          machine.defective_quantity ??
          0
        )
      : null;

  const workOrderRemainingQuantity =
    hasActiveWorkOrder
      ? Number(
          machine.remaining_quantity ??
          Math.max(
            Number(machine.work_order_quantity ?? 0) -
            Number(machine.work_order_produced ?? 0),
            0
          )
        )
      : null;

  const workOrderProgress =
    hasActiveWorkOrder && workOrderTargetQuantity > 0
      ? Math.min(
          (workOrderProducedQuantity / workOrderTargetQuantity) * 100,
          100
        )
      : 0;

  // RC6.6E — Üretim & Kalite kartı üst Canlı SCADA ile aynı kaynaktan









  const reworkRequiredQuantity =
    hasActiveWorkOrder
      ? Number(machine.rework_required_quantity || 0)
      : null;

  const operationStatusColor =
    operationStatus === "offline"
      ? t.accent.red
      : operationStatus === "completed"
      ? t.accent.green
      : operationStatus === "quality_pending"
      ? t.accent.yellow
      : operationStatus === "running"
      ? t.accent.green
      : t.accent.blue;


  // MACHINES_HISTORY_LOCALIZATION_RC1
  const normalizeHistoryKey = (value) =>
    String(value ?? "")
      .trim()
      .toLocaleLowerCase("tr-TR")
      .replace(/ı/g, "i")
      .replace(/\s+/g, "_");

  const localizeMaintenanceType = (value) => {
    const key = normalizeHistoryKey(value);

    const labels = {
      preventive: {
        tr: "Periyodik Bakım",
        en: "Preventive Maintenance",
      },
      periodic: {
        tr: "Periyodik Bakım",
        en: "Periodic Maintenance",
      },
      periyodik: {
        tr: "Periyodik Bakım",
        en: "Preventive Maintenance",
      },
      corrective: {
        tr: "Düzeltici Bakım",
        en: "Corrective Maintenance",
      },
      breakdown: {
        tr: "Arıza Bakımı",
        en: "Breakdown Maintenance",
      },
      repair: {
        tr: "Onarım",
        en: "Repair",
      },
      ariza: {
        tr: "Arıza Bakımı",
        en: "Breakdown Maintenance",
      },
      predictive: {
        tr: "Kestirimci Bakım",
        en: "Predictive Maintenance",
      },
    };

    return (
      labels[key]?.[currentLang] ||
      labels[key]?.tr ||
      value ||
      "—"
    );
  };

  const localizeFaultType = (value, reasonCode) => {
    const code = String(reasonCode ?? "")
      .trim()
      .toUpperCase();

    const key = normalizeHistoryKey(value);

    if (code === "AUTO_FAULT") {
      return currentLang === "en"
        ? "Automatic Machine Fault"
        : "Otomatik Makine Arızası";
    }

    const labels = {
      automatic_machine_fault: {
        tr: "Otomatik Makine Arızası",
        en: "Automatic Machine Fault",
      },
      machine_fault: {
        tr: "Makine Arızası",
        en: "Machine Fault",
      },
      fault: {
        tr: "Makine Arızası",
        en: "Machine Fault",
      },
      breakdown: {
        tr: "Makine Arızası",
        en: "Machine Breakdown",
      },
    };

    return (
      labels[key]?.[currentLang] ||
      labels[key]?.tr ||
      value ||
      "—"
    );
  };

  const localizeHistoryDescription = (value) => {
    const text = String(value ?? "").trim();

    const labels = {
      "Tool holder vibration alarm": {
        tr: "Takım tutucu titreşim alarmı",
        en: "Tool holder vibration alarm",
      },
      "Spindle vibration threshold warning": {
        tr: "İş mili titreşim eşik uyarısı",
        en: "Spindle vibration threshold warning",
      },
      "Automatic Machine Fault": {
        tr: "Otomatik Makine Arızası",
        en: "Automatic Machine Fault",
      },
    };

    return labels[text]?.[currentLang] || text || "—";
  };


  // MACHINES_FINAL_HISTORY_PARTS_I18N_RC1

  const localizeSparePartName = (value) => {
    const text = String(value ?? "").trim();

    const labels = {
      "İş Mili Rulman Seti": {
        tr: "İş Mili Rulman Seti",
        en: "Spindle Bearing Set",
      },
      "Takım Tutucu BT40": {
        tr: "Takım Tutucu BT40",
        en: "BT40 Tool Holder",
      },
      "Soğutma Pompası": {
        tr: "Soğutma Pompası",
        en: "Coolant Pump",
      },
    };

    return labels[text]?.[currentLang] || text || "—";
  };

  const localizeUnit = (value) => {
    const key = String(value ?? "")
      .trim()
      .toLocaleLowerCase("tr-TR");

    const units = {
      adet: {
        tr: "adet",
        en: "pcs",
      },
      set: {
        tr: "set",
        en: "set",
      },
      takım: {
        tr: "takım",
        en: "set",
      },
    };

    return units[key]?.[currentLang] || value || "";
  };

  const localizeStatusLogMessage = (item) => {
    if (!item) return "—";

    const message = String(item.message ?? "").trim();

    if (message) {
      const translated = localizeHistoryDescription(message);

      if (translated !== message) {
        return translated;
      }
    }

    const reasonCode = String(
      item.reason_code ?? ""
    ).trim().toUpperCase();

    if (reasonCode === "AUTO_FAULT") {
      return currentLang === "en"
        ? "Automatic machine fault"
        : "Otomatik makine arızası";
    }

    if (
      item.reason_code === "first_piece_setup" ||
      item.reason_name === "İlk parça ayarı"
    ) {
      return currentLang === "en"
        ? "Planned downtime: First-piece setup"
        : "Planlı duruş: İlk parça ayarı";
    }

    if (item.event_type === "production") {
      return currentLang === "en"
        ? "Production operation is in progress"
        : "Üretim operasyonu devam ediyor";
    }

    if (item.old_status || item.new_status) {
      const localizeStatusValue = (status) => {
        const key = String(status ?? "")
          .trim()
          .toLowerCase();

        if (!key) return "—";

        if (key === "setup") {
          return currentLang === "en"
            ? "Setup"
            : "Ayar";
        }

        if (key === "running") {
          return currentLang === "en"
            ? "Running"
            : "Çalışıyor";
        }

        if (key === "idle") {
          return currentLang === "en"
            ? "Idle"
            : "Bekliyor";
        }

        if (key === "fault") {
          return currentLang === "en"
            ? "Fault"
            : "Arıza";
        }

        if (key === "stopped") {
          return currentLang === "en"
            ? "Stopped"
            : "Durdu";
        }

        return statusLabels[status] || status || "—";
      };

      const oldLabel = localizeStatusValue(
        item.old_status
      );

      const newLabel = localizeStatusValue(
        item.new_status
      );

      return `${oldLabel} → ${newLabel}`;
    }

    return message || item.reason_name || "—";
  };

  const PassportRow = ({ label, value }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0",
      borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
      <span style={{ color: t.text.label }}>{label}</span>
      <span style={{ color: t.text.primary, fontWeight: 500, textAlign: "right", maxWidth: "60%" }}>{value || "—"}</span>
    </div>
  );

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: t.bg.overlay, backdropFilter: "blur(6px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: t.bg.modal, border: `1px solid ${t.border.default}`,
        borderRadius: 18,
        width: "96vw",
        height: "94vh",
        maxWidth: "none",
        maxHeight: "none",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: t.shadow.modal,
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: t.bg.header,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{
              width: 48, height: 48, borderRadius: 12,
              background: liveColors.bg,
              border: `1px solid ${liveColors.color}55`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
            }}>⚙️</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: t.text.primary }}>
                {machine.name || machine.machine_name}
              </div>
              <div style={{ color: t.text.muted, fontSize: 13, marginTop: 2 }}>
                {machine.machine_id || machine.id} · {machine.type || machine.machine_type || "CNC"}
              </div>
            </div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}>
              <Badge status={liveState} />

              <span style={{
                padding: "5px 9px",
                borderRadius: 7,
                background: t.bg.metricCard,
                border: `1px solid ${t.border.default}`,
                color: t.text.secondary,
                fontSize: 11,
                fontWeight: 700,
              }}>
                PROGRAM: {liveProgram}
              </span>

              <span style={{
                padding: "5px 9px",
                borderRadius: 7,
                background: t.bg.metricCard,
                border: `1px solid ${t.border.default}`,
                color: t.text.secondary,
                fontSize: 11,
                fontWeight: 700,
              }}>
                MOD: {liveMode}
              </span>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => onEdit(machine)} style={{
              padding: "8px 16px", borderRadius: 8,
              border: `1px solid ${t.accent.blueBorder}`,
              background: t.accent.blueBg, color: t.accent.blue,
              cursor: "pointer", fontSize: 13,
            }}>✏️ {m.edit}</button>
            <button onClick={onClose} style={{
              padding: "8px 16px", borderRadius: 8,
              border: `1px solid ${t.border.input}`,
              background: t.bg.toggle, color: t.text.secondary,
              cursor: "pointer", fontSize: 13,
            }}>✕ {m.close}</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: "grid",
            gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
            padding: "0 18px",
            height: 58,
            minHeight: 58,
            flexShrink: 0,
          borderBottom: `1px solid ${t.border.divider}`,
          background: t.bg.header,
            overflow: "hidden",
        }}>
          {tabs.map(tab_ => (
            <button key={tab_.id} onClick={() => setTab(tab_.id)} style={{
              minWidth: 0,
                height: 58,
                padding: "0 5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "none",
                border: "none",
                cursor: "pointer",
              color: tab === tab_.id ? t.accent.blue : t.text.muted, fontSize: 13, fontWeight: 600,
              borderBottom: `2px solid ${tab === tab_.id ? t.accent.blue : "transparent"}`,
              whiteSpace: "normal",
                textAlign: "center",
                lineHeight: 1.15,
                overflow: "hidden",
                transition: "all 0.2s",
            }}>{tab_.label}</button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          ref={detailScrollRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 28,
            background: t.bg.modal,
          }}
        >

          {/* OVERVIEW */}
          {tab === "overview" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              
                {/* SCADA_LIVE_PANEL_V3 */}

                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 14,
                  flexWrap: "wrap",
                  padding: "12px 16px",
                  background: t.bg.metricCard,
                  border: `1px solid ${t.border.default}`,
                  borderRadius: 12,
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}>
                    <span style={{
                      width: 11,
                      height: 11,
                      borderRadius: "50%",
                      background: isOffline
                        ? t.accent.red
                        : liveColors.color,
                      boxShadow: `0 0 10px ${
                        isOffline
                          ? t.accent.red
                          : liveColors.color
                      }`,
                    }} />

                    <span style={{
                      color: isOffline
                        ? t.accent.red
                        : liveColors.color,
                      fontSize: 13,
                      fontWeight: 900,
                    }}>
                      {isOffline
                        ? "OFFLINE"
                        : statusLabels[liveState] ||
                          String(liveState).toUpperCase()}
                    </span>
                  </div>

                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 22,
                    flexWrap: "wrap",
                  }}>
                    {[
                      ["PROGRAM", liveProgram, t.accent.blue],
                      ["MOD", liveMode, t.accent.purple],
                      [m.lastData, liveLastSeen, t.text.secondary],
                    ].map(([label, value, color]) => (
                      <div key={label} style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 7,
                      }}>
                        <span style={{
                          color: t.text.muted,
                          fontSize: 9,
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                        }}>
                          {label}
                        </span>

                        <span style={{
                          color,
                          fontSize: label === m.lastData ? 11 : 13,
                          fontWeight: 900,
                        }}>
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(310px, 1fr))",
                  gap: 16,
                }}>
                  {[
                    {
                      label: m.spindleSpeed,
                      value: liveSpindle,
                      rawValue:
                        machine.spindle_speed ??
                        machine.spindle_rpm ??
                        machine.rpm,
                      maxValue:
                        machine.max_spindle_rpm ??
                        machine.spindle_max_rpm ??
                        6000,
                      unit: "RPM",
                      color: t.accent.purple,
                      trendPoints: rpmTrendPoints,
                      peak: rpmPeak,
                    },
                    {
                      label: m.feedProgress,
                      value: liveFeed,
                      rawValue: machine.feed_rate,
                      maxValue:
                        machine.max_feed_rate ??
                        machine.feed_rate_max ??
                        30000,
                      unit: "mm/dk",
                      color: t.accent.blue,
                      trendPoints: feedTrendPoints,
                      peak: feedPeak,
                    },
                  ].map(item => (
                    <div key={item.label} style={{
                      minHeight: 210,
                      background: t.bg.metricCard,
                      border: `1px solid ${
                        isOffline
                          ? t.border.default
                          : item.color + "55"
                      }`,
                      borderRadius: 18,
                      padding: "24px 28px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      position: "relative",
                      overflow: "hidden",
                    }}>
                      <div style={{
                        position: "absolute",
                        top: 16,
                        left: 18,
                        color: t.text.muted,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: "0.1em",
                      }}>
                        {item.label}
                      </div>

                      <ScadaGauge
                        value={item.rawValue}
                        displayValue={item.value}
                        unit={item.unit}
                        maxValue={item.maxValue}
                        color={item.color}
                        offline={isOffline}
                        t={t}
                      />

                      <div style={{
                        width: "100%",
                        marginTop: 24,
                        paddingTop: 14,
                        borderTop: `1px solid ${t.border.subtle}`,
                      }}>
                        <div style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 8,
                        }}>
                          <span style={{
                            color: t.text.muted,
                            fontSize: 9,
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                          }}>
                            {m.last60Measurements}
                          </span>

                          <span style={{
                            color: t.text.secondary,
                            fontSize: 10,
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                          }}>
                            MAKS: {
                              !isOffline && item.peak != null
                                ? Number(item.peak).toLocaleString(
                                    "tr-TR",
                                    { maximumFractionDigits: 0 }
                                  )
                                : "--"
                            } {item.peak != null && !isOffline ? item.unit : ""}
                          </span>
                        </div>

                        <div style={{
                          height: 48,
                          width: "100%",
                          position: "relative",
                        }}>
                          {!isOffline && item.trendPoints ? (
                            <svg
                              viewBox="0 0 100 40"
                              preserveAspectRatio="none"
                              style={{
                                width: "100%",
                                height: "48px",
                                overflow: "visible",
                              }}
                            >
                              <line
                                x1="0"
                                y1="38"
                                x2="100"
                                y2="38"
                                stroke={t.border.subtle}
                                strokeWidth="0.7"
                              />

                              <line
                                x1="0"
                                y1="20"
                                x2="100"
                                y2="20"
                                stroke={t.border.subtle}
                                strokeWidth="0.45"
                                strokeDasharray="2 3"
                              />

                              <polyline
                                points={item.trendPoints}
                                fill="none"
                                stroke={item.color}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                vectorEffect="non-scaling-stroke"
                              />
                            </svg>
                          ) : (
                            <div style={{
                              height: 48,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: t.text.placeholder,
                              fontSize: 10,
                              fontWeight: 700,
                            }}>
                              {m.waitingForLiveData}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(170px, 1fr))",
                  gap: 12,
                }}>
                  {[
                    [m.productionCard, liveProduction, m.pcsUnit, t.accent.green],
                    [m.cycleCard, liveCycle, "", t.accent.yellow],
                    [m.targetCard, liveTarget, m.pcsUnit, t.accent.blue],
                    [m.scrapCard, liveDefective, m.pcsUnit, t.accent.red],
                  ].map(([label, value, unit, color]) => (
                    <div key={label} style={{
                      background: t.bg.metricCard,
                      border: `1px solid ${t.border.default}`,
                      borderRadius: 14,
                      padding: "16px 18px",
                    }}>
                      <div style={{
                        color: t.text.muted,
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: "0.09em",
                        marginBottom: 9,
                      }}>
                        {label}
                      </div>

                      <div style={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 6,
                      }}>
                        <span style={{
                          color: isOffline
                            ? t.text.placeholder
                            : color,
                          fontSize: 30,
                          lineHeight: 1,
                          fontWeight: 900,
                          fontVariantNumeric: "tabular-nums",
                        }}>
                          {value}
                        </span>

                        {value !== "--" && unit && (
                          <span style={{
                            color: t.text.muted,
                            fontSize: 10,
                            fontWeight: 700,
                          }}>
                            {unit}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {isOffline ? (
                  <div style={{
                    background: t.accent.redBg,
                    border: `1px solid ${t.accent.red}66`,
                    borderRadius: 13,
                    padding: "15px 18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                  }}>
                    <div>
                      <div style={{
                        color: t.accent.red,
                        fontSize: 14,
                        fontWeight: 900,
                      }}>
                        {m.machineUnreachable}
                      </div>

                      <div style={{
                        color: t.text.secondary,
                        fontSize: 11,
                        marginTop: 4,
                      }}>
                        {m.telemetryDisconnected}
                        {" "}{liveLastSeen}
                      </div>
                    </div>

                    <span style={{
                      color: t.accent.red,
                      fontSize: 24,
                    }}>
                      ⚠
                    </span>
                  </div>
                ) : hasLiveAlarm ? (
                  <div style={{
                    background: t.accent.redBg,
                    border: `1px solid ${t.accent.red}77`,
                    borderRadius: 13,
                    padding: "16px 18px",
                  }}>
                    <div style={{
                      color: t.accent.red,
                      fontSize: 13,
                      fontWeight: 900,
                      marginBottom: 6,
                    }}>
                      ⚠ {m.activeFanucAlarm}
                    </div>

                    <div style={{
                      color: t.accent.red,
                      fontSize: 22,
                      fontWeight: 950,
                    }}>
                      {machine.alarm_code || "ALARM"}
                    </div>

                    <div style={{
                      color: t.text.primary,
                      fontSize: 12,
                      marginTop: 5,
                    }}>
                      {machine.alarm_message ||
                        "Makinede aktif alarm bulunuyor."}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    background: liveColors.bg,
                    border: `1px solid ${liveColors.color}44`,
                    borderRadius: 12,
                    padding: "11px 16px",
                    color: liveColors.color,
                    fontSize: 12,
                    fontWeight: 850,
                  }}>
                    ✓ {m.connectionActiveNoAlarm}
                  </div>
                )}

                {/* SCADA_LIVE_PANEL_V4 */}

                <div style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(320px, 1fr))",
                  gap: 14,
                }}>
                  {/* {m.activeWorkOrder} */}
                  <div style={{
                    minHeight: 180,
                    background: t.bg.metricCard,
                    border: `1px solid ${t.accent.green}45`,
                    borderRadius: 15,
                    padding: 20,
                    boxShadow: `inset 0 1px 0 ${t.accent.green}12`,
                  }}>
                    <ScadaPanelTitle
                      icon="▣"
                      color={t.accent.green}
                      t={t}
                    >
                      {m.activeWorkOrder}
                    </ScadaPanelTitle>

                    <div style={{
                      color: activeWorkOrderNo === "—"
                        ? t.text.placeholder
                        : t.text.primary,
                      fontSize: 25,
                      lineHeight: 1.1,
                      fontWeight: 950,
                      letterSpacing: "-0.02em",
                      marginBottom: 13,
                    }}>
                      {activeWorkOrderNo}
                    </div>

                    <ScadaDataRow
                      label={m.product}
                      value={activeProduct}
                      t={t}
                    />

                    <ScadaDataRow
                      label={m.operator}
                      value={activeOperator}
                      color={t.accent.green}
                      t={t}
                    />

                    <ScadaDataRow
                      label={m.workOrderRemaining}
                      value={
                        workOrderRemainingQuantity != null
                          ? `${workOrderRemainingQuantity.toLocaleString(getLocale())} ${m.pcsUnit}`
                          : "—"
                      }
                      color={t.accent.yellow}
                      strong
                      t={t}
                    />
                  </div>

                  {/* MES RC2 - Üretim ve Kalite Durumu */}
                  <div style={{
                    minHeight: 180,
                    background: t.bg.metricCard,
                    border: `1px solid ${operationStatusColor}45`,
                    borderRadius: 15,
                    padding: 20,
                    boxShadow: `inset 0 1px 0 ${operationStatusColor}12`,
                  }}>
                    <ScadaPanelTitle
                      icon="◆"
                      color={operationStatusColor}
                      t={t}
                    >
                      {m.productionQuality}
                    </ScadaPanelTitle>

                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      marginBottom: 16,
                      padding: "9px 11px",
                      borderRadius: 9,
                      background: `${operationStatusColor}12`,
                      border: `1px solid ${operationStatusColor}35`,
                    }}>
                      <span style={{
                        color: t.text.muted,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: "0.08em",
                      }}>
                        OPERASYON
                      </span>

                      <span style={{
                        color: operationStatusColor,
                        fontSize: 12,
                        fontWeight: 900,
                        textAlign: "right",
                      }}>
                        {operationStatusLabel}
                      </span>
                    </div>

                    {hasActiveWorkOrder ? (
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 9,
                      }}>
                        {[
                          ["Hedef", workOrderTargetQuantity, t.accent.blue],
                          [m.produced, workOrderProducedQuantity, t.accent.green],
                          [m.scrapCard, workOrderDefectiveQuantity, t.accent.red],
                          ["Kalan", workOrderRemainingQuantity, t.accent.yellow],
                        ].map(([label, value, color]) => (
                          <div key={label} style={{
                            padding: "10px 11px",
                            borderRadius: 9,
                            background: t.bg.modal,
                            border: `1px solid ${t.border.subtle}`,
                          }}>
                            <div style={{
                              color: t.text.muted,
                              fontSize: 9,
                              fontWeight: 800,
                              letterSpacing: "0.06em",
                              marginBottom: 5,
                            }}>
                              {label}
                            </div>

                            <div style={{
                              color,
                              fontSize: 18,
                              fontWeight: 950,
                              fontVariantNumeric: "tabular-nums",
                            }}>
                              {Number(value || 0).toLocaleString(getLocale())}
                              <span style={{
                                color: t.text.muted,
                                fontSize: 9,
                                fontWeight: 700,
                                marginLeft: 4,
                              }}>
                                adet
                              </span>
                            </div>
                          </div>
                        ))}

                        <div style={{
                          gridColumn: "1 / -1",
                          marginTop: 4,
                          padding: "11px 12px",
                          borderRadius: 9,
                          background: t.bg.modal,
                          border: `1px solid ${t.border.subtle}`,
                        }}>
                          <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            marginBottom: 8,
                          }}>
                            <span style={{
                              color: t.text.muted,
                              fontSize: 10,
                              fontWeight: 800,
                              letterSpacing: "0.06em",
                            }}>
                              İLERLEME
                            </span>

                            <span style={{
                              color: t.accent.green,
                              fontSize: 12,
                              fontWeight: 900,
                              fontVariantNumeric: "tabular-nums",
                            }}>
                              %{workOrderProgress.toLocaleString(getLocale(), {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                              })}
                            </span>
                          </div>

                          <div style={{
                            height: 7,
                            borderRadius: 999,
                            overflow: "hidden",
                            background: t.bg.toggle,
                          }}>
                            <div style={{
                              height: "100%",
                              width: `${workOrderProgress}%`,
                              borderRadius: 999,
                              background: t.accent.green,
                              transition: "width 0.35s ease",
                            }} />
                          </div>

                          <div style={{
                            marginTop: 7,
                            color: t.text.muted,
                            fontSize: 10,
                            fontWeight: 700,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                          }}>
                            {workOrderProducedQuantity.toLocaleString(getLocale())}
                            {" / "}
                            {workOrderTargetQuantity.toLocaleString(getLocale())}
                            {" adet"}
                          </div>
                        </div>

                        {reworkRequiredQuantity > 0 && (
                          <div style={{
                            gridColumn: "1 / -1",
                            padding: "9px 11px",
                            borderRadius: 9,
                            background: t.accent.redBg,
                            border: `1px solid ${t.accent.red}45`,
                            color: t.accent.red,
                            fontSize: 11,
                            fontWeight: 850,
                          }}>
                            ↻ {m.reworkRequired}{" "}
                            {reworkRequiredQuantity.toLocaleString(getLocale())} {m.pcsUnit}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{
                        minHeight: 105,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        borderRadius: 10,
                        background: t.bg.modal,
                        border: `1px dashed ${t.border.default}`,
                        padding: 16,
                      }}>
                        <div style={{
                          color: t.text.secondary,
                          fontSize: 12,
                          fontWeight: 850,
                        }}>
                          {m.noActiveWorkOrder}
                        </div>

                        <div style={{
                          color: t.text.muted,
                          fontSize: 10,
                          marginTop: 5,
                          lineHeight: 1.5,
                        }}>
                          {m.workOrderDataHint}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* {m.cncProgram} */}
                  <div style={{
                    minHeight: 180,
                    background: t.bg.metricCard,
                    border: `1px solid ${t.accent.blue}45`,
                    borderRadius: 15,
                    padding: 20,
                    boxShadow: `inset 0 1px 0 ${t.accent.blue}12`,
                  }}>
                    <ScadaPanelTitle
                      icon="⌘"
                      color={t.accent.blue}
                      t={t}
                    >
                      {m.cncProgram}
                    </ScadaPanelTitle>

                    <div style={{
                      color:
                        liveProgram === "—" || liveProgram === "--"
                          ? t.text.placeholder
                          : t.accent.blue,
                      fontSize: 25,
                      lineHeight: 1.1,
                      fontWeight: 950,
                      letterSpacing: "-0.02em",
                      marginBottom: 13,
                    }}>
                      {liveProgram}
                    </div>

                    <ScadaDataRow
                      label={m.programName}
                      value={programName}
                      t={t}
                    />

                    <ScadaDataRow
                      label={m.tool}
                      value={activeTool}
                      color={t.accent.purple}
                      t={t}
                    />

                    <ScadaDataRow
                      label={m.lineBlock}
                      value={programLine}
                      color={t.accent.yellow}
                      t={t}
                    />
                  </div>
                </div>

                <div style={{ display: "none", gap: 12, flexWrap: "wrap" }}>
                <MetricCard label="OEE" value={machine.oee} unit="%" color={t.accent.green} icon="📊" />
                <MetricCard label="MTBF" value={
                    machine.mtbf_hours != null
                      ? Number(machine.mtbf_hours) < 0.1
                        ? Number(machine.mtbf_hours).toFixed(2)
                        : Number(machine.mtbf_hours).toFixed(1)
                      : undefined
                  } unit={m.hours} color={t.accent.blue} icon="⏱️" sub={m.avgMtbf} />
                <MetricCard label="MTTR" value={
                    machine.mttr_hours != null
                      ? Number(machine.mttr_hours) < 0.1
                        ? Number(machine.mttr_hours).toFixed(2)
                        : Number(machine.mttr_hours).toFixed(1)
                      : undefined
                  } unit={m.hours} color={t.accent.yellow} icon="🔧" sub={m.avgMttr} />
                <MetricCard label={m.maintenanceCost} value={machine.total_maintenance_cost} unit="₺" color={t.accent.purple} icon="💰" />
              </div>
              <div style={{ display: "none", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 16,
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>📋 {m.passport}</div>
                  {[
                    [m.serialNo,            machine.serial_number || machine.serial_no],
                    [m.brandModel,      `${machine.brand || "—"} / ${machine.model || "—"}`],
                    [m.location,           machine.location],
                    [m.productionLine,       machine.production_line],
                    [m.purchaseDate,         fmt(machine.purchase_date)],
                    [m.warrantyEnd,      fmt(machine.warranty_end)],
                    [m.lastMaintenance,          fmt(machine.last_maintenance)],
                    [m.nextMaintenance,      fmt(machine.next_maintenance)],
                    [m.authOperators,  machine.authorized_operators || (m.all)],
                  ].map(([k, v]) => <PassportRow key={k} label={k} value={v} />)}
                </div>

                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 16,
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>📡 {m.liveParams}</div>
                  {[
                    [m.temperature,    machine.temperature != null ? `${machine.temperature.toFixed(1)} °C` : "—",   machine.temperature > 80 ? t.accent.red : t.accent.green],
                    [m.vibration,    machine.vibration   != null ? `${machine.vibration.toFixed(2)} mm/s` : "—",   machine.vibration > 5 ? t.accent.yellow : t.accent.green],
                    [m.livePower, machine.power_kw != null ? `${Number(machine.power_kw).toFixed(1)} kW` : "—", t.accent.blue],
                    [m.rpm, machine.rpm         != null ? machine.rpm : "—",                               t.accent.purple],
                    [m.cycleTime,   machine.cycle_time        != null ? `${machine.cycle_time} sn` : "—",        t.accent.blue],
                    [m.targetCycle,    machine.target_cycle_time  != null ? `${machine.target_cycle_time} sn` : "—", t.text.muted],
                    [m.riskScore,  machine.risk_score  != null ? machine.risk_score.toFixed(0) : "—",             machine.risk_score > 70 ? t.accent.red : machine.risk_score > 40 ? t.accent.yellow : t.accent.green],
                    [m.opcua, machine.connectivity || (m.notConnected),                     machine.connectivity ? t.accent.green : t.accent.red],
                  ].map(([k, v, color]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between",
                      padding: "8px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                      <span style={{ color: t.text.label }}>{k}</span>
                      <span style={{ color, fontWeight: 600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MAKİNE BİLGİLERİ */}
          {tab === "machineinfo" && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 20,
            }}>
              <div style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 12,
              }}>
                <MetricCard
                  label="OEE"
                  value={machine.oee}
                  unit="%"
                  color={t.accent.green}
                  icon="📊"
                />

                <MetricCard
                  label="MTBF"
                  value={machine.mtbf_hours?.toFixed(1)}
                  unit={m.hours}
                  color={t.accent.blue}
                  icon="⏱️"
                  sub={m.avgMtbf}
                />

                <MetricCard
                  label="MTTR"
                  value={machine.mttr_hours?.toFixed(1)}
                  unit={m.hours}
                  color={t.accent.yellow}
                  icon="🔧"
                  sub={m.avgMttr}
                />

                <MetricCard
                  label={m.maintenanceCost}
                  value={machine.total_maintenance_cost}
                  unit="₺"
                  color={t.accent.purple}
                  icon="💰"
                />
              </div>

              <div style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(340px, 1fr))",
                gap: 16,
              }}>
                <div style={{
                  background: t.bg.metricCard,
                  border: `1px solid ${t.border.default}`,
                  borderRadius: 14,
                  padding: 20,
                }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: t.text.label,
                    marginBottom: 16,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}>
                    📋 {m.passport}
                  </div>

                  {[
                    [
                      m.serialNo,
                      machine.serial_number,
                    ],
                    [
                      m.brandModel,
                      `${machine.brand || "—"} / ${machine.model || "—"}`,
                    ],
                    [
                      m.location,
                      machine.location,
                    ],
                    [
                      m.productionLine,
                      machine.production_line,
                    ],
                    [
                      m.purchaseDate,
                      fmt(machine.purchase_date),
                    ],
                    [
                      m.warrantyEnd,
                      fmt(machine.warranty_end),
                    ],
                    [
                      m.lastMaintenance,
                      fmt(machine.last_maintenance),
                    ],
                    [
                      m.nextMaintenance,
                      fmt(machine.next_maintenance),
                    ],
                    [
                      m.authOperators,
                      machine.authorized_operators || (m.all),
                    ],
                  ].map(([key, value]) => (
                    <PassportRow
                      key={key}
                      label={key}
                      value={value}
                    />
                  ))}
                </div>

                <div style={{
                  background: t.bg.metricCard,
                  border: `1px solid ${t.border.default}`,
                  borderRadius: 14,
                  padding: 20,
                }}>
                  <div style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: t.text.label,
                    marginBottom: 16,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}>
                    📡 {m.liveParams}
                  </div>

                  {[
                    [
                      m.temperature,
                      machine.temperature != null &&
                      Number(machine.temperature) !== 0
                        ? `${Number(machine.temperature).toFixed(1)} °C`
                        : "—",
                      Number(machine.temperature) > 80
                        ? t.accent.red
                        : t.accent.green,
                    ],
                    [
                      m.vibration,
                      machine.vibration != null
                        ? `${Number(machine.vibration).toFixed(2)} mm/s`
                        : "—",
                      Number(machine.vibration) > 5
                        ? t.accent.yellow
                        : t.accent.green,
                    ],
                    [
                      m.livePower,
                      machine.power_kw != null
                        ? `${Number(machine.power_kw).toFixed(1)} kW`
                        : "—",
                      t.accent.blue,
                    ],
                    [
                      m.rpm,
                      machine.spindle_rpm != null
                        ? Number(machine.spindle_rpm).toFixed(0)
                        : machine.rpm != null
                          ? Number(machine.rpm).toFixed(0)
                          : "—",
                      t.accent.purple,
                    ],
                    [
                      m.feedRate,
                      machine.feed_rate != null
                        ? Number(machine.feed_rate).toFixed(0)
                        : "—",
                      t.accent.green,
                    ],
                    [
                      m.program,
                      machine.program_no || "—",
                      t.accent.blue,
                    ],
                    [
                      m.programName,
                      machine.program_name || "—",
                      t.accent.blue,
                    ],
                    [
                      m.machinePartCounter || m.partCount,
                      machine.part_count != null
                        ? Number(machine.part_count).toLocaleString(getLocale())
                        : "—",
                      t.accent.green,
                    ],
                    [
                      m.alarm,
                      Number(machine.alarm_code || 0) > 0
                        ? machine.alarm_message || String(machine.alarm_code)
                        : m.none,
                      Number(machine.alarm_code || 0) > 0
                        ? t.accent.red
                        : t.accent.green,
                    ],
                    [
                      m.lastSeen,
                      machine.last_seen
                        ? new Date(machine.last_seen).toLocaleString(
                            getLocale(),
                            {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            second: "2-digit",
                          })
                        : "—",
                      t.text.secondary,
                    ],
                    [
                      m.connection,
                      machine.connection_status || "—",
                      machine.connection_status === "online"
                        ? t.accent.green
                        : t.accent.red,
                    ],
                    [
                      m.driver,
                      machine.driver || "—",
                      t.text.secondary,
                    ],
                  ].map(([key, value, color]) => (
                    <div
                      key={key}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 20,
                        padding: "10px 0",
                        borderBottom:
                          `1px solid ${t.border.subtle}`,
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: t.text.label }}>
                        {key}
                      </span>

                      <span style={{
                        color,
                        fontWeight: 700,
                        textAlign: "right",
                      }}>
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* OEE */}
          {tab === "oee" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              <div style={{
                display: "grid",
                gridTemplateColumns:
                  "minmax(340px, 1.2fr) minmax(300px, 1fr)",
                gap: 16,
              }}>
                <div style={{
                  background: t.bg.metricCard,
                  border: `1px solid ${t.border.default}`,
                  borderRadius: 14,
                  padding: 20,
                }}>
                  <ScadaPanelTitle
                    icon="◉"
                    color={t.accent.purple}
                    t={t}
                  >
                    {m.liveOee}
                  </ScadaPanelTitle>

                  <div style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(4, minmax(70px, 1fr))",
                    alignItems: "start",
                    justifyItems: "center",
                    gap: 10,
                  }}>
                    <ScadaOeeRing
                      label={m.availability}
                      value={liveAvailability}
                      color={t.accent.green}
                      t={t}
                    />

                    <ScadaOeeRing
                      label={m.performance}
                      value={livePerformance}
                      color={t.accent.blue}
                      t={t}
                    />

                    <ScadaOeeRing
                      label={m.quality}
                      value={liveQuality}
                      color={t.accent.purple}
                      t={t}
                    />

                    <ScadaOeeRing
                      label="OEE"
                      value={liveOee}
                      color={t.accent.yellow}
                      t={t}
                    />
                  </div>
                </div>

                <div style={{
                  background: t.bg.metricCard,
                  border: `1px solid ${t.border.default}`,
                  borderRadius: 14,
                  padding: 20,
                }}>
                  <ScadaPanelTitle
                    icon="↔"
                    color={t.text.secondary}
                    t={t}
                  >
                    {m.statusTimeline}
                  </ScadaPanelTitle>

                  <ScadaStatusTimeline
                    items={statusLog}
                    currentStatus={liveState}
                    statusLabels={statusLabels}
                    themeName={themeName}
                    t={t}
                    m={m}
                  />

                  <div style={{
                    marginTop: 18,
                    paddingTop: 12,
                    borderTop: `1px solid ${t.border.subtle}`,
                    color: t.text.muted,
                    fontSize: 10,
                  }}>
                    {m.lastSeen}:
                    <span style={{
                      marginLeft: 6,
                      color: t.text.secondary,
                      fontWeight: 800,
                    }}>
                      {liveLastSeen}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <OeeGauge value={machine.oee} t={t} />
                <div style={{ display: "flex", gap: 12, flex: 1, flexWrap: "wrap" }}>
                  <MetricCard label={m.availability} value={machine.availability} unit="%" color={t.accent.green} />
                  <MetricCard label={m.performance}       value={machine.performance}  unit="%" color={t.accent.blue} />
                  <MetricCard label={m.quality}          value={machine.quality_rate} unit="%" color={t.accent.purple} />
                </div>
              </div>
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 16,
                  textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {m.oee7days}
                </div>
                {oeeHistory.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={oeeHistory}>
                      <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
                      <XAxis dataKey="date" stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} tickFormatter={v => v?.slice(5)} />
                      <YAxis stroke={t.chart.axis} tick={{ fontSize: 11, fill: t.chart.axis }} domain={[0, 100]} />
                      <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 8, color: t.text.primary }} />
                      <Legend />
                      <Line type="monotone" dataKey="oee" stroke={t.accent.green} strokeWidth={2} dot={false} name="OEE %" />
                      <Line type="monotone" dataKey="availability" stroke={t.accent.blue} strokeWidth={2} dot={false} name={`${m.availability} %`} />
                      <Line type="monotone" dataKey="performance" stroke={t.accent.yellow} strokeWidth={2} dot={false} name={`${m.performance} %`} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: "center", color: t.text.placeholder, padding: "40px 0" }}>
                    {m.noOeeHistory}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ARIZA */}
          {tab === "faults" && (
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: t.bg.theadRow }}>
                    {[m.date, m.faultType, m.description, m.duration, m.resolution].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11,
                        fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {faults.length === 0
                    ? <tr><td colSpan={5} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{m.noFaults}</td></tr>
                    : faults.map((f, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: t.text.secondary }}>{fmtDT(f.fault_time || f.created_at)}</td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: t.accent.red, fontWeight: 600 }}>
                          {localizeFaultType(
                            f.fault_type || f.type,
                            f.reason_code
                          )}
                        </td>

                        <td style={{ padding: "12px 16px", fontSize: 13, color: t.text.primary }}>
                          {localizeHistoryDescription(
                            f.description || f.message
                          )}
                        </td>

                        <td style={{ padding: "12px 16px", fontSize: 13, color: t.accent.yellow }}>
                          {(
                            f.duration_minutes ??
                            f.duration_min
                          ) != null
                            ? `${Number(
                                f.duration_minutes ??
                                f.duration_min
                              ).toLocaleString(
                                getLocale(),
                                { maximumFractionDigits: 1 }
                              )} ${m.minutesShort}`
                            : "—"}
                        </td>

                        <td style={{ padding: "12px 16px", fontSize: 12, color: t.text.muted }}>
                          {f.resolution ||
                            (
                              f.status === "closed"
                                ? m.resolvedClosed
                                : "—"
                            )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* BAKIM */}
          {tab === "maintenance" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {maintenances.length === 0
                ? <div style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{m.noMaintenance}</div>
                : maintenances.map((mx, i) => (
                  <div key={i} style={{
                    background: t.bg.metricCard, border: `1px solid ${t.border.default}`,
                    borderRadius: 12, padding: "16px 20px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                  }}>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          color: t.text.primary,
                          marginBottom: 5,
                        }}
                      >
                        {mx.title ||
                          localizeMaintenanceType(
                            mx.maintenance_type || mx.type
                          )}
                      </div>

                      <div
                        style={{
                          color: t.text.secondary,
                          fontSize: 12,
                          marginBottom: 6,
                        }}
                      >
                        {localizeMaintenanceType(
                          mx.maintenance_type || mx.type
                        )}
                      </div>

                      <div
                        style={{
                          color: t.text.muted,
                          fontSize: 12,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 12,
                        }}
                      >
                        {(mx.actual_hours ?? mx.estimated_hours) != null && (
                          <span>
                            {m.durationLabel}:{" "}
                            <strong style={{ color: t.text.secondary }}>
                              {Number(
                                mx.actual_hours ??
                                mx.estimated_hours
                              ).toLocaleString(
                                getLocale(),
                                { maximumFractionDigits: 1 }
                              )}{" "}
                              {m.hoursShort}
                            </strong>
                          </span>
                        )}

                        {mx.total_cost != null && (
                          <span>
                            {m.costLabel}:{" "}
                            <strong style={{ color: t.accent.yellow }}>
                              {Number(mx.total_cost).toLocaleString(
                                getLocale(),
                                { maximumFractionDigits: 0 }
                              )} ₺
                            </strong>
                          </span>
                        )}

                        {mx.description && (
                          <span>
                            {localizeHistoryDescription(mx.description)}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          color: t.text.secondary,
                        }}
                      >
                        {mx.status === "completed"
                          ? m.maintenanceCompletionLabel
                          : m.eventDateLabel}:{" "}
                        {fmt(
                          mx.status === "completed"
                            ? (
                                mx.completed_at ||
                                mx.started_at ||
                                mx.created_at ||
                                mx.date
                              )
                            : (
                                mx.scheduled_date ||
                                mx.started_at ||
                                mx.created_at ||
                                mx.date
                              )
                        )}
                      </div>

                      <div style={{ marginTop: 8 }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 10px",
                            borderRadius: 20,
                            fontSize: 11,
                            fontWeight: 700,
                            background:
                              mx.status === "completed"
                                ? t.accent.greenBg
                                : mx.status === "pending"
                                ? t.accent.yellowBg
                                : t.bg.tag,
                            color:
                              mx.status === "completed"
                                ? t.accent.green
                                : mx.status === "pending"
                                ? t.accent.yellow
                                : t.text.secondary,
                          }}
                        >
                          {mx.status === "completed"
                            ? m.completedLabel
                            : mx.status === "pending"
                            ? m.pendingLabel
                            : mx.status === "in_progress"
                            ? m.inProgressLabel
                            : mx.status || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* ALARMLAR */}
          {tab === "alarms" && (
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: t.bg.theadRow }}>
                    {[m.datetime, m.level, m.message, m.statusLabel].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11,
                        fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {alarms.length === 0
                    ? <tr><td colSpan={4} style={{ textAlign: "center", color: t.text.placeholder, padding: 40 }}>{m.noAlarms}</td></tr>
                    : alarms.map((a, i) => (
                      <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: t.text.secondary }}>{fmtDT(a.timestamp || a.created_at)}</td>
                        <td style={{ padding: "12px 16px" }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                            background:
                              (a.level || a.alarm_level || a.severity) === "critical"
                                ? t.accent.redBg
                                : t.accent.yellowBg,
                            color:
                              (a.level || a.alarm_level || a.severity) === "critical"
                                ? t.accent.red
                                : t.accent.yellow,
                          }}>
                            {(() => {
                              const level =
                                a.level ||
                                a.alarm_level ||
                                a.severity;

                              if (level === "critical") {
                                return m.criticalUpper;
                              }

                              if (level === "warning") {
                                return m.warningUpper;
                              }

                              return m.infoUpper;
                            })()}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 13, color: t.text.primary }}>
                          {localizeHistoryDescription(a.message)}
                        </td>
                        <td style={{ padding: "12px 16px", fontSize: 12, color: a.acknowledged ? t.accent.green : t.text.muted }}>
                          {a.acknowledged ? `✓ ${m.acknowledged}` : m.waiting}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* YEDEK PARÇA */}
          {tab === "parts" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {spareParts.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: t.text.placeholder,
                    padding: 40,
                  }}
                >
                  {m.noPartsLinked}
                </div>
              ) : (
                spareParts.map((p, i) => {
                  const currentStock = Number(
                    p.current_stock ?? 0
                  );

                  const minStock = Number(
                    p.min_stock ?? 0
                  );

                  const isLowStock =
                    currentStock <= minStock;

                  const isCritical =
                    Number(p.is_critical || 0) === 1;

                  return (
                    <div
                      key={p.id || i}
                      style={{
                        background: t.bg.metricCard,
                        border: `1px solid ${
                          isLowStock
                            ? t.accent.yellow
                            : t.border.default
                        }`,
                        borderRadius: 12,
                        padding: "14px 20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 20,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 8,
                            marginBottom: 5,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              color: t.text.primary,
                            }}
                          >
                            {localizeSparePartName(
                              p.part_name || p.name
                            )}
                          </span>

                          {isCritical && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 800,
                                padding: "2px 7px",
                                borderRadius: 20,
                                background: t.accent.redBg,
                                color: t.accent.red,
                              }}
                            >
                              {m.criticalUpper}
                            </span>
                          )}

                          {isLowStock && (
                            <span
                              style={{
                                fontSize: 10,
                                fontWeight: 800,
                                padding: "2px 7px",
                                borderRadius: 20,
                                background: t.accent.yellowBg,
                                color: t.accent.yellow,
                              }}
                            >
                              {m.minimumStockUpper}
                            </span>
                          )}
                        </div>

                        <div
                          style={{
                            color: t.text.muted,
                            fontSize: 12,
                          }}
                        >
                          {m.stockCode}:{" "}
                          <strong
                            style={{
                              color: t.text.secondary,
                            }}
                          >
                            {p.stock_code || "—"}
                          </strong>

                          {p.supplier_name && (
                            <>
                              {" · "}
                              {m.supplierLabel}
                              :{" "}
                              <strong
                                style={{
                                  color: t.text.secondary,
                                }}
                              >
                                {p.supplier_name}
                              </strong>
                            </>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 800,
                            color: isLowStock
                              ? t.accent.yellow
                              : t.accent.green,
                          }}
                        >
                          {m.stock}:{" "}
                          {currentStock.toLocaleString(
                            getLocale()
                          )}{" "}
                          {localizeUnit(p.unit)}
                        </div>

                        <div
                          style={{
                            color: t.text.muted,
                            fontSize: 11,
                            marginTop: 3,
                          }}
                        >
                          {m.minimumLabel}
                          :{" "}
                          {minStock.toLocaleString(
                            getLocale()
                          )}
                        </div>

                        {p.unit_price != null && (
                          <div
                            style={{
                              color: t.text.muted,
                              fontSize: 11,
                              marginTop: 3,
                            }}
                          >
                            {m.unitPriceLabel}
                            :{" "}
                            {Number(
                              p.unit_price
                            ).toLocaleString(
                              getLocale(),
                              {
                                maximumFractionDigits: 0,
                              }
                            )}{" "}
                            ₺
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* DURUM GÜNLÜĞÜ */}
          {tab === "statuslog" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              {displayStatusLog.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: t.text.placeholder,
                    padding: 40,
                  }}
                >
                  {m.noStatusLog}
                </div>
              ) : (
                displayStatusLog.map((entry, i) => {
                  const newStatus =
                    entry.new_status ||
                    entry.status ||
                    "unknown";

                  const oldStatus =
                    entry.old_status;

                  const newC = getStatusColors(
                    newStatus,
                    themeName
                  );

                  const oldC = getStatusColors(
                    oldStatus,
                    themeName
                  );

                  const durationSeconds =
                    Number(
                      entry.duration_seconds || 0
                    );

                  const durationMinutes =
                    durationSeconds > 0
                      ? Math.round(
                          (durationSeconds / 60) * 10
                        ) / 10
                      : null;

                  return (
                    <div
                      key={entry.uid || entry.id || i}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 16,
                        padding: "12px 0",
                        borderBottom:
                          `1px solid ${t.border.subtle}`,
                      }}
                    >
                      <span
                        style={{
                          marginTop: 4,
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          flexShrink: 0,
                          background: newC.color,
                          boxShadow:
                            `0 0 6px ${newC.color}`,
                        }}
                      />

                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 13,
                            color: t.text.primary,
                            fontWeight: 600,
                          }}
                        >
                          {oldStatus && (
                            <>
                              <span
                                style={{
                                  color: oldC.color,
                                }}
                              >
                                {oldStatus === "setup"
                                  ? m.statusSetup
                                  : statusLabels[oldStatus] ||
                                    oldStatus}
                              </span>

                              <span
                                style={{
                                  color: t.text.muted,
                                  margin: "0 7px",
                                }}
                              >
                                →
                              </span>
                            </>
                          )}

                          <span
                            style={{
                              color: newC.color,
                            }}
                          >
                            {newStatus === "setup"
                              ? m.statusSetup
                              : statusLabels[newStatus] ||
                                newStatus}
                          </span>
                        </div>

                        <div
                          style={{
                            marginTop: 4,
                            fontSize: 12,
                            color: t.text.secondary,
                          }}
                        >
                          {localizeStatusLogMessage(
                            entry
                          )}
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 12,
                            marginTop: 5,
                            fontSize: 11,
                            color: t.text.muted,
                          }}
                        >
                          <span>
                            {fmtDT(
                              entry.event_time ||
                              entry.created_at ||
                              entry.started_at
                            )}
                          </span>

                          {durationMinutes != null && (
                            <span>
                              {m.durationLabel}
                              :{" "}
                              {durationMinutes.toLocaleString(
                                getLocale(),
                                {
                                  maximumFractionDigits: 1,
                                }
                              )}{" "}
                              {m.minutesShort}
                            </span>
                          )}

                          {entry.operator_name && (
                            <span>
                              {m.operatorLabel}
                              : {entry.operator_name}
                            </span>
                          )}

                          {entry.alarm_code &&
                            entry.alarm_code !== "0" &&
                            String(entry.alarm_code).toUpperCase() !== "AUTO_FAULT" && (
                              <span
                                style={{
                                  color: t.accent.yellow,
                                }}
                              >
                                {m.codeLabel}
                                : {entry.alarm_code}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

// MACHINE_FORM_STABLE_FIELD_FINAL_RC1
const MachineFormField = ({
  label,
  fieldKey,
  type = "text",
  opts,
  disabled = false,
  value,
  onChangeValue,
  inputStyle,
  labelStyle,
  optionBackground,
}) => {
  return (
    <div>
      <label style={labelStyle}>
        {label}
      </label>

      {opts ? (
        <select
          value={value ?? ""}
          disabled={disabled}
          onChange={(event) =>
            onChangeValue(
              fieldKey,
              event.target.value
            )
          }
          style={{
            ...inputStyle,
            opacity: disabled ? 0.65 : 1,
          }}
        >
          {opts.map((option) => (
            <option
              key={option.v}
              value={option.v}
              style={{
                background: optionBackground,
              }}
            >
              {option.l}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          value={value ?? ""}
          disabled={disabled}
          onChange={(event) =>
            onChangeValue(
              fieldKey,
              event.target.value
            )
          }
          style={{
            ...inputStyle,
            opacity: disabled ? 0.65 : 1,
          }}
        />
      )}
    </div>
  );
};


// ─── MACHINE FORM MODAL ───────────────────────────────────────────────────
const MachineFormModal = ({ machine, onClose, onSaved }) => {
  const { t } = useTheme();
  const { t: l } = useLanguage();
  const m = l.machinesx || {};
  const isEdit = !!machine;


  const [form, setForm] = useState({
    name:                 machine?.name || machine?.machine_name || "",
    machine_id:           machine?.machine_id || machine?.id || "",
    type:                 machine?.type || machine?.machine_type || "CNC",
    location:             machine?.location || "",
    production_line:      machine?.production_line || "",
    serial_number:        machine?.serial_number || machine?.serial_no || "",
    brand:                machine?.brand || "",
    model:                machine?.model || "",
    purchase_date:        machine?.purchase_date?.slice(0, 10) || "",
    warranty_end:         machine?.warranty_end?.slice(0, 10) || "",
    status:               machine?.status || "stopped",
    authorized_operators: machine?.authorized_operators || "",
    notes:                machine?.notes || "",

  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (key, value) => {
    setForm(previous => ({
      ...previous,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (!form.name || !form.machine_id) {
      setError(
        m.errNameIdRequired
      );
      return;
    }


    const payload = {
      ...form,
      machine_name: form.name,
      machine_type: form.type,
      serial_no: form.serial_number,
    };

    setLoading(true);
    setError("");

    try {
      const url = isEdit
        ? `${API}/machines/${encodeURIComponent(form.machine_id)}`
        : `${API}/machines`;

      const response = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: headers(),
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          body.detail ||
          m.errGeneric ||
          "Makine kaydedilemedi"
        );
      }

      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    background: t.bg.input,
    border: `1px solid ${t.border.input}`,
    borderRadius: 8,
    color: t.text.primary,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    color: t.text.label,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 6,
  };

  const statusLabels = getStatusLabels();

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: t.bg.overlay,
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: t.bg.modal,
          border: `1px solid ${t.border.default}`,
          borderRadius: 20,
          width: "100%",
          maxWidth: 820,
          maxHeight: "92vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: t.shadow.modal,
        }}
        onClick={event => event.stopPropagation()}
      >
        <div
          style={{
            padding: "20px 28px",
            borderBottom: `1px solid ${t.border.default}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: t.bg.header,
          }}
        >
          <div
            style={{
              fontSize: 17,
              fontWeight: 700,
              color: t.text.primary,
            }}
          >
            {isEdit
              ? `✏️ ${m.editMachine}`
              : `➕ ${m.addMachine}`}
          </div>

          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: t.text.muted,
              cursor: "pointer",
              fontSize: 20,
            }}
          >
            ✕
          </button>
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 28,
            background: t.bg.modal,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            <MachineFormField
              value={form["name"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={`${m.machineName} *`}
              fieldKey="name"
            />

            <MachineFormField
              value={form["machine_id"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={`${m.machineId} *`}
              fieldKey="machine_id"
              disabled={isEdit}
            />

            <MachineFormField
              value={form["type"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.machineType}
              fieldKey="type"
              opts={[
                { v: "CNC", l: "CNC" },
                { v: "Lathe", l: m.lathe },
                { v: "Press", l: m.press_ },
                { v: "Welding", l: m.welding },
                { v: "Assembly", l: m.assembly },
                { v: "Conveyor", l: m.conveyor },
                { v: "Other", l: m.other },
              ]}
            />

            <MachineFormField
              value={form["status"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.statusLabel}
              fieldKey="status"
              opts={Object.entries(statusLabels).map(
                ([value, label]) => ({
                  v: value,
                  l: label,
                })
              )}
            />

            <MachineFormField
              value={form["location"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.location}
              fieldKey="location"
            />

            <MachineFormField
              value={form["production_line"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.productionLine}
              fieldKey="production_line"
            />

            <MachineFormField
              value={form["serial_number"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.serialNo}
              fieldKey="serial_number"
            />

            <MachineFormField
              value={form["brand"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.brand}
              fieldKey="brand"
            />

            <MachineFormField
              value={form["model"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.model}
              fieldKey="model"
            />

            <MachineFormField
              value={form["purchase_date"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.purchaseDate}
              fieldKey="purchase_date"
              type="date"
            />

            <MachineFormField
              value={form["warranty_end"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.warrantyEnd}
              fieldKey="warranty_end"
              type="date"
            />

            <MachineFormField
              value={form["authorized_operators"]}
              onChangeValue={set}
              inputStyle={inputStyle}
              labelStyle={labelStyle}
              optionBackground={t.bg.modal}
              label={m.authOperators}
              fieldKey="authorized_operators"
            />
          </div>


          <div style={{ marginTop: 16 }}>
            <label style={labelStyle}>
              {m.notes}
            </label>

            <textarea
              value={form.notes}
              onChange={event =>
                set("notes", event.target.value)
              }
              rows={3}
              style={{
                ...inputStyle,
                resize: "vertical",
              }}
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: 12,
                padding: "10px 16px",
                background: t.accent.redBg,
                border: `1px solid ${t.accent.red}44`,
                borderRadius: 8,
                color: t.accent.red,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            padding: "16px 28px",
            borderTop: `1px solid ${t.border.default}`,
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            background: t.bg.header,
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              borderRadius: 8,
              border: `1px solid ${t.border.input}`,
              background: "none",
              color: t.text.secondary,
              cursor: "pointer",
              fontSize: 13,
            }}
          >
            {m.cancel}
          </button>

          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: "10px 24px",
              borderRadius: 8,
              border: "none",
              background: loading
                ? t.bg.tag
                : "linear-gradient(135deg,#3b82f6,#6366f1)",
              color: "#fff",
              cursor: loading ? "default" : "pointer",
              fontSize: 13,
              fontWeight: 600,
              boxShadow: loading ? "none" : t.shadow.btn,
            }}
          >
            {loading
              ? m.saving
              : isEdit
                ? m.update
                : m.add}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MACHINE CARD ──────────────────────────────────────────────────────────
const MachineCard = ({ m: mac, onClick }) => {
  const { t, themeName } = useTheme();
  const { t: l, lang } = useLanguage();
  const lm = l.machinesx || {};

  // MACHINES_CLOCK_SAFE_P7
  // Card connection state must use API-server-time-derived state.
  const isOffline = Boolean(mac?._is_offline);
  const isStale = Boolean(mac?._is_stale);

  const cardStatus = String(
    mac?.status || "unknown"
  ).trim().toLowerCase();

  const cardAlarmCode = Number(mac?.alarm_code ?? 0);

  const cardHasAlarmMessage =
    typeof mac?.alarm_message === "string" &&
    mac.alarm_message.trim() !== "" &&
    mac.alarm_message.trim() !== "0";

  const hasAlarm =
    !isOffline &&
    (
      cardStatus === "fault" ||
      cardStatus === "alarm" ||
      (Number.isFinite(cardAlarmCode) && cardAlarmCode > 0) ||
      cardHasAlarmMessage
    );

  const displayStatus = deriveMachineDisplayStatus(mac);

  const sc = getStatusColors(displayStatus, themeName);

  const oee = Number(mac.oee ?? 0);
  const oeeColor =
    oee >= 85
      ? t.accent.green
      : oee >= 65
        ? t.accent.yellow
        : t.accent.red;

  const lastSeen = mac.last_seen
    ? `${fmtDT(mac.last_seen)} · ${formatLastSeenAge(mac)}`
    : lm.dataUnavailable;

  /*
   * ÖNEMLİ:
   * production alanı güncel/iş emri üretim miktarı için kullanılır.
   * FANUC toplam sayacı olan eski 31240 değeri burada ayrıca türetilmez.
   */
  const machineProduction =
    mac.production_count ??
    mac.production;

  const productionValue =
    !isOffline && !isStale && machineProduction != null
      ? Number(machineProduction).toLocaleString(getLocale())
      : "—";

  const cycleValue =
    !isOffline && !isStale && mac.cycle_count != null
      ? Number(mac.cycle_count).toLocaleString(getLocale())
      : "—";

  return (
    <div
      onClick={() => onClick(mac)}
      style={{
        background: t.bg.card,
        border: `1px solid ${hasAlarm ? t.accent.red + "66" : t.border.card}`,
        borderRadius: 16,
        padding: 20,
        cursor: "pointer",
        transition: "all 0.22s",
        position: "relative",
        overflow: "hidden",
        boxShadow: hasAlarm
          ? `0 0 0 1px ${t.accent.red}22, ${t.shadow.card}`
          : t.shadow.card,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = `${sc.color}88`;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow =
          `0 12px 32px rgba(0,0,0,0.15), 0 0 0 1px ${sc.color}33`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor =
          hasAlarm ? `${t.accent.red}66` : t.border.card;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = hasAlarm
          ? `0 0 0 1px ${t.accent.red}22, ${t.shadow.card}`
          : t.shadow.card;
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: hasAlarm
            ? t.accent.red
            : `linear-gradient(90deg, ${sc.color}, transparent)`,
        }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: t.text.primary,
              marginBottom: 4,
            }}
          >
            {mac.name || mac.machine_name}
          </div>

          <div style={{ fontSize: 12, color: t.text.muted }}>
            {mac.machine_id || mac.id}
            {" · "}
            {mac.type || mac.machine_type || "CNC"}
          </div>
        </div>

        <Badge status={displayStatus} />
      </div>

      {isOffline ? (
        <div
          style={{
            background: t.bg.metricCard,
            border: `1px solid ${t.border.subtle}`,
            borderRadius: 10,
            padding: 14,
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 800,
              color: t.text.secondary,
              marginBottom: 5,
            }}
          >
            {lm.liveDataUnavailable}
          </div>

          <div style={{ fontSize: 11, color: t.text.muted }}>
            {lm.telemetryNoNewData}
          </div>

          <div
            style={{
              fontSize: 11,
              color: t.text.placeholder,
              marginTop: 8,
            }}
          >
            {lm.lastSeen}: {lastSeen}
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 6,
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  color: t.text.label,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                OEE
              </span>

              <span
                style={{
                  fontSize: 13,
                  fontWeight: 800,
                  color: oeeColor,
                }}
              >
                {oee.toFixed(1)}%
              </span>
            </div>

            <div
              style={{
                height: 6,
                background: t.bg.tag,
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.max(0, Math.min(100, oee))}%`,
                  borderRadius: 3,
                  background:
                    `linear-gradient(90deg, ${oeeColor}88, ${oeeColor})`,
                  transition: "width 0.8s ease",
                }}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8,
            }}
          >
            {[
              {
                label: lm.spindleCard,
                value:
                  mac.spindle_speed != null
                    ? `${Number(mac.spindle_speed).toFixed(0)} rpm`
                    : "—",
              },
              {
                label: lm.feedCard,
                value:
                  mac.feed_rate != null
                    ? `${Number(mac.feed_rate).toFixed(0)} mm/dk`
                    : "—",
              },
              {
                label: lm.cycleCard,
                value: cycleValue,
              },
              {
                label: lm.productionCard,
                value: productionValue,
              },
              {
                label: lm.scrapCard,
                value:
                  mac.defective != null
                    ? Number(mac.defective).toLocaleString(getLocale())
                    : "—",
                warn: Number(mac.defective) > 0,
              },
              {
                label: lm.temperatureCard,
                value:
                  mac.temperature != null
                    ? `${Number(mac.temperature).toFixed(0)}°C`
                    : "—",
                warn: Number(mac.temperature) > 80,
              },
            ].map(({ label, value, warn }) => (
              <div
                key={label}
                style={{
                  background: warn
                    ? t.accent.redBg
                    : t.bg.metricCard,
                  borderRadius: 8,
                  padding: "8px 10px",
                  border:
                    `1px solid ${
                      warn
                        ? t.accent.red + "44"
                        : t.border.subtle
                    }`,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    color: t.text.muted,
                    marginBottom: 3,
                    textTransform: "uppercase",
                  }}
                >
                  {label}
                </div>

                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: warn
                      ? t.accent.red
                      : t.text.secondary,
                  }}
                >
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 14,
              borderRadius: 10,
              padding: "11px 12px",
              background: hasAlarm
                ? t.accent.redBg
                : t.bg.metricCard,
              border:
                `1px solid ${
                  hasAlarm
                    ? t.accent.red + "55"
                    : t.border.subtle
                }`,
            }}
          >
            {hasAlarm ? (
              <>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: t.accent.red,
                    marginBottom: 6,
                    letterSpacing: "0.04em",
                  }}
                >
                  ⚠ {lm.activeAlarmUpper}
                </div>

                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 800,
                    color: t.accent.red,
                  }}
                >
                  {mac.alarm_code || lm.alarmFallback || "Alarm"}
                </div>

                {(mac.alarm_message || String(mac.alarm_code || "").toUpperCase() === "SV0410") && (
                  <div
                    style={{
                      fontSize: 12,
                      color: t.text.secondary,
                      marginTop: 4,
                      lineHeight: 1.4,
                    }}
                  >
                    {machineAlarmDisplayMessage(mac, lm)}
                  </div>
                )}
              </>
            ) : (
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: t.accent.green,
                }}
              >
                ✓ {lm.noActiveAlarm}
              </div>
            )}
          </div>

          <div
            style={{
              marginTop: 12,
              fontSize: 11,
              color: t.text.placeholder,
            }}
          >
            {lm.lastSeen}: {lastSeen}
          </div>
        </>
      )}

      <div
        style={{
          marginTop: 12,
          paddingTop: 12,
          borderTop: `1px solid ${t.border.subtle}`,
          display: "flex",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span style={{ fontSize: 11, color: t.text.placeholder }}>
          📍 {machineLocationDisplay(mac.location, lang)}
        </span>

        <span style={{ fontSize: 11, color: t.text.placeholder }}>
          {lm.risk}:{" "}
          <span
            style={{
              color:
                mac.risk_score > 70
                  ? t.accent.red
                  : mac.risk_score > 40
                    ? t.accent.yellow
                    : t.accent.green,
              fontWeight: 700,
            }}
          >
            {mac.risk_score != null
              ? Math.round(mac.risk_score)
              : "—"}
          </span>
        </span>
      </div>
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────
const machineLocationDisplay = (location, lang) => {
  const value = String(location || "").trim();

  if (!value) return "—";

  if (lang === "en") {
    const match = value.match(/^Hat\s+(\d+)$/i);

    if (match) {
      return `Line ${match[1]}`;
    }
  }

  return value;
};

const machineAlarmDisplayMessage = (mac, lm) => {
  const code = String(mac?.alarm_code || "").trim().toUpperCase();

  if (code === "SV0410") {
    return lm.alarmSv0410 || mac?.alarm_message || "";
  }

  return mac?.alarm_message || "";
};

export default function Machines() {
  const { t } = useTheme();
  const { t: l, lang } = useLanguage();
  const m = useMemo(() => l.machinesx || {}, [l.machinesx]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [search, setSearch]     = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType]     = useState("all");
  const [view, setView] = useState(() => {
  return localStorage.getItem("machines_view") || "grid";
});
  const [selected, setSelected] = useState(null);

  // Açık makine detay ekranını yeni listeyle güncelle
  useEffect(() => {
    setSelected(previous => {
      if (!previous) return previous;

      const selectedId = previous.machine_id || previous.id;

      const freshMachine = machines.find(
        mac => (mac.machine_id || mac.id) === selectedId
      );

      return freshMachine || previous;
    });
  }, [machines]);
  const [editing, setEditing]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [stats, setStats]       = useState({});

  const fetchMachines = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/machines`, { headers: headers() });
      if (!res.ok) throw new Error(m.errFetch);
      const data = await res.json();
      const rawList = Array.isArray(data) ? data : data.machines || data.items || [];

      // MACHINES_CLOCK_SAFE_P3
      // Freshness hesabını browser saatine değil API server saatine bağla.
      const serverNow =
        !Array.isArray(data) && data?.server_time
          ? new Date(data.server_time).getTime()
          : NaN;

      const now = Number.isFinite(serverNow)
        ? serverNow
        : Date.now();

      const list = rawList.map(mac => withMachineRuntimeState(mac, now));

      setMachines(list);

      const sc = list.reduce((a, mac) => {
        const status = mac._display_status || mac.status || "unknown";
        a[status] = (a[status] || 0) + 1;
        return a;
      }, {});
      const avgOee = list.length ? (list.reduce((s, mac) => s + (mac.oee || 0), 0) / list.length).toFixed(1) : 0;
      setStats({ total: list.length, ...sc, avgOee });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [m]);

  useEffect(() => { fetchMachines(); }, [fetchMachines]);
  useEffect(() => { const id = setInterval(fetchMachines, 3000); return () => clearInterval(id); }, [fetchMachines]);

  const filtered = machines.filter(mac => {
    const matchSearch = !search ||
      (mac.name || mac.machine_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (mac.machine_id || mac.id || "").toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || mac.status === filterStatus;
    const matchType   = filterType === "all"   || (mac.type || mac.machine_type) === filterType;
    return matchSearch && matchStatus && matchType;
  });

  const types = [...new Set(machines.map(mac => mac.type || mac.machine_type).filter(Boolean))];
  const statusLabels = getStatusLabels();

  const selectStyle = {
    padding: "10px 14px", background: t.bg.input,
    border: `1px solid ${t.border.input}`, borderRadius: 10,
    color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer",
  };

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes sf-pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes sf-spin  { to{transform:rotate(360deg)} }
        ::-webkit-scrollbar { width:6px; height:6px }
        ::-webkit-scrollbar-track { background: var(--scrollbar-track, #f1f5f9) }
        ::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb, #cbd5e1); border-radius:3px }
      `}</style>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0, letterSpacing: "-0.02em" }}>
              ⚙️ {m.title}
            </h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {m.subtitle}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchMachines} style={{
              padding: "10px 16px", borderRadius: 10,
              border: `1px solid ${t.border.input}`, background: t.bg.toggle,
              color: t.text.secondary, cursor: "pointer", fontSize: 13,
            }}>🔄 {m.refresh}</button>
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={{
              padding: "10px 20px", borderRadius: 10, border: "none",
              background: "linear-gradient(135deg,#3b82f6,#6366f1)",
              color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600,
              boxShadow: t.shadow.btn,
            }}>+ {m.addMachine}</button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            { label: m.total,       value: stats.total || 0,                                          color: t.accent.blue,   icon: "🏭" },
            { label: m.running_,    value: stats.running || 0,                                        color: t.accent.green,  icon: "▶" },
            { label: m.faultMaintenance, value: (stats.maintenance || 0) + (stats.fault || 0),            color: t.accent.red,    icon: "⚠️" },
            { label: m.avgOee,     value: `${stats.avgOee || 0}%`,                                   color: t.accent.yellow, icon: "📊" },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1, minWidth: 140, background: t.bg.card,
              border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "16px 20px",
              display: "flex", alignItems: "center", gap: 14, boxShadow: t.shadow.card,
            }}>
              <span style={{ fontSize: 24 }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: t.text.muted, fontWeight: 600 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <input
            placeholder={`🔍 ${m.searchPlaceholder}`}
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...selectStyle, flex: 1, minWidth: 220 }}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="all">{m.allStatuses}</option>
            {Object.entries(statusLabels).map(([v, label]) => (
              <option key={v} value={v}>{label}</option>
            ))}
          </select>
          {types.length > 0 && (
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectStyle}>
              <option value="all">{m.allTypes}</option>
              {types.map(ty => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          )}
          <div style={{ display: "flex", gap: 4, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 10, padding: 4 }}>
            {[["grid", "⊞"], ["list", "≡"]].map(([v, icon]) => (
              <button key={v} onClick={() => {
                  setView(v);
                  localStorage.setItem("machines_view", v);
                }} style={{
                padding: "6px 12px", borderRadius: 7, border: "none", cursor: "pointer",
                background: view === v ? t.bg.toggleActive : "none",
                color: view === v ? t.text.primary : t.text.muted, fontSize: 16,
              }}>{icon}</button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{m.loadingMachines}</span>
          </div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: "center", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 14, color: t.accent.red }}>
            ⚠️ {error}
            <br />
            <button onClick={fetchMachines} style={{ marginTop: 12, padding: "8px 16px", borderRadius: 8, border: `1px solid ${t.accent.red}44`, background: "none", color: t.accent.red, cursor: "pointer", fontSize: 13 }}>
              {m.retry}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", color: t.text.placeholder, padding: "80px 0" }}>
            {machines.length === 0 ? m.noMachinesYet : m.noMatch}
          </div>
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 16 }}>
            {filtered.map(mac => <MachineCard key={mac.machine_id || mac.id} m={mac} onClick={setSelected} />)}
          </div>
        ) : (
          <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden", boxShadow: t.shadow.card }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: t.bg.theadRow }}>
                  {[m.machine_, m.machineType, m.location_, m.statusLabel, "OEE %", m.temperature, m.vibration, m.riskScore, m.actions].map(h => (
                    <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((mac, i) => {
                  const oee = mac.oee ?? 0;
                  const oeeColor = oee >= 85 ? t.accent.green : oee >= 65 ? t.accent.yellow : t.accent.red;
                  return (
                    <tr key={mac.machine_id || mac.id} style={{ borderTop: `1px solid ${t.border.subtle}`, background: i % 2 === 0 ? t.bg.rowAlt : t.bg.row }}>
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ fontWeight: 600, color: t.text.primary, fontSize: 13 }}>{mac.name || mac.machine_name}</div>
                        <div style={{ color: t.text.muted, fontSize: 11 }}>{mac.machine_id || mac.id}</div>
                      </td>
                      <td style={{ padding: "14px 16px", color: t.text.secondary, fontSize: 13 }}>{mac.type || mac.machine_type || "—"}</td>
                      <td style={{ padding: "14px 16px", color: t.text.secondary, fontSize: 13 }}>{machineLocationDisplay(mac.location, lang)}</td>
                      <td style={{ padding: "14px 16px" }}><Badge status={mac.status} /></td>
                      <td style={{ padding: "14px 16px", fontWeight: 700, color: oeeColor }}>{oee}%</td>
                      <td style={{ padding: "14px 16px", color: mac.temperature > 80 ? t.accent.red : t.text.secondary, fontSize: 13 }}>
                        {mac.temperature != null ? `${mac.temperature.toFixed(1)}°C` : "—"}
                      </td>
                      <td style={{ padding: "14px 16px", color: mac.vibration > 5 ? t.accent.yellow : t.text.secondary, fontSize: 13 }}>
                        {mac.vibration != null ? mac.vibration.toFixed(2) : "—"}
                      </td>
                      <td style={{ padding: "14px 16px", fontWeight: 700, fontSize: 13,
                        color: mac.risk_score > 70 ? t.accent.red : mac.risk_score > 40 ? t.accent.yellow : t.accent.green }}>
                        {mac.risk_score != null ? Math.round(mac.risk_score) : "—"}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => setSelected(mac)} style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, color: t.accent.blue }}>
                            {m.detail}
                          </button>
                          <button onClick={() => { setEditing(mac); setShowForm(true); }} style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer", background: t.bg.toggle, border: `1px solid ${t.border.input}`, color: t.text.secondary }}>✏️</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && (
          <div style={{ marginTop: 16, color: t.text.placeholder, fontSize: 12, textAlign: "right" }}>
            {filtered.length} / {machines.length} {m.machineShowing}
          </div>
        )}
      </div>

      {selected && (
        <MachineDetailModal machine={selected} onClose={() => setSelected(null)}
          onEdit={(mac) => { setEditing(mac); setShowForm(true); }} />
      )}
      {showForm && (
        <MachineFormModal machine={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchMachines(); }} />
      )}
    </div>
  );
}